/**
 * Space Configurator — Catálogo de gamas (dataset agnóstico de gama)
 *
 * Registro de datos + lógica específica de cada gama de producto (RIVET PRO,
 * VERSATILE, sin tornillos, Media Carga...). El motor (space-configurator.js) es genérico y
 * SOLO llama a los métodos de esta interfaz — no conoce nombres de nodo GLB,
 * fórmulas de SKU ni matrices de medidas de ninguna gama en concreto.
 *
 * Añadir una gama nueva = añadir una entrada a GAMAS. No toca el motor.
 *
 * Contrato de cada entrada de GAMAS:
 *   id, label            — identificador y nombre visible (respaldo castellano)
 *   labelKey, useCaseKey  — claves planas i18n del nombre y del "apta para qué".
 *                           El catálogo NO llama a t() (se carga antes que el helper):
 *                           solo declara claves; el motor resuelve
 *                           t(labelKey) || label   y   t(useCaseKey) || description.
 *   capacity(cfg)          — capacidad de ESA config → { value, unitKey } y, opcional,
 *                            { count, countUnitKey } (hoy solo palés, Fase 3)
 *   capacityMax()          — máximo de la gama, mismo formato (tarjeta de gama)
 *   loadMode             — 'scene' (GLB único/por-medida + visibilidad por nodo)
 *                           | 'kit' (piezas nombradas clonadas y ensambladas)
 *   fields[]              — esquema de campos configurables (para la UI genérica)
 *   defaultConfig()        — config inicial válida
 *   nivelesFor(cfg)        — niveles válidos para la config actual (array)
 *   footprintMM(cfg)       — {width,depth,height} en mm reales (verdad física)
 *   resolveGlbUrl(cfg,urls)   [loadMode='scene'] — URL del GLB a cargar
 *   kitUrl(urls)              [loadMode='kit']    — URL del kit
 *   applyVisibility(model,cfg) + applyColor(model,cfg)   [loadMode='scene']
 *   assemble(kitIndex,cfg,THREE)                          [loadMode='kit'] → THREE.Group ya coloreado
 *   buildHandle(cfg)       — handle Shopify determinista
 *   deriveSku(cfg)         — SKU determinista (resolución cross-market)
 *   searchQueries(cfg)     — cadena de queries de búsqueda (fallback SKU)
 *   summary(cfg)           — texto corto para la lista de BOM/presupuesto
 *   chain                  — [opcional] gamas con módulos encadenados:
 *                            { full, shared, matchKeys[], sideGapMM }
 *                            full   = el módulo que lleva sus dos bastidores
 *                            shared = el que comparte el bastidor del vecino
 *                            matchKeys = campos de cfg que definen el bastidor
 *                                        COMPARTIDO (los que deben coincidir)
 *                            sideGapMM = separación entre módulos encadenados (0)
 *   canUseUnitType(...)    — [opcional] derivable de `chain` con chainCompatible()
 */
(function () {

  function p3(n) { n = String(parseInt(n, 10) || 0); return n.length >= 3 ? n : ('000' + n).slice(-3); }

  // ¿Se puede usar este tipo de módulo con lo que ya hay en la sala? Un módulo que
  // COMPARTE bastidor necesita que exista al menos uno COMPLETO y compatible ("sin
  // inicial no se añade adicional nunca", regla del usuario). Vive aquí, compartido,
  // para que dos gamas con cadena no puedan implementar criterios distintos: ese
  // desajuste ya se dio una vez (Media Carga comparaba alto+fondo y palés alto+ancho,
  // y el motor solo miraba el de Media Carga).
  function chainCompatible(gama, unitType, cfg, allItems) {
    var ch = gama && gama.chain;
    if (!ch || unitType !== ch.shared) return true;
    return (allItems || []).some(function (it) {
      if (it.gamaId !== gama.id) return false;
      if ((it.cfg.unitType || ch.full) !== ch.full) return false;
      for (var i = 0; i < ch.matchKeys.length; i++) {
        var k = ch.matchKeys[i];
        if (String(it.cfg[k]) !== String(cfg[k])) return false;
      }
      return true;
    });
  }
  function fmtDim(mm) { return (mm / 10).toString().replace(/\.0$/, ''); } // mm → cm sin decimales sobrantes

  // El catálogo NO puede llamar a t() (se carga antes del motor), así que las
  // funciones que producen texto visible reciben el traductor como argumento:
  // `summary(cfg, tr)`. `tr` puede faltar (prototipo/harness) o no conocer la
  // clave, y t() devuelve el NOMBRE DE LA CLAVE cuando no la encuentra — sin
  // esta comprobación se colaría un "v_mod_inicial" en pantalla, que es
  // exactamente el fallo que ya se dio con `gama_bullrack_label`.
  function tr_(tr, key, fallback) {
    if (!tr || !key) return fallback;
    var s = tr(key);
    return (s && s !== key) ? s : fallback;
  }

  var GAMAS = {};

  // ════════════════════════════════════════════════════════════════════════
  // RIVET PRO — GLB por anchura, visibilidad por nodo (nivel/profundidad/tipoBalda)
  // ════════════════════════════════════════════════════════════════════════
  (function () {
    var WIDTHS_MM = { '90': 900, '120': 1200, '150': 1500, '180': 1800 };
    var DEPTHS_MM = { '45': 450, '60': 600 };
    var ALTURA_NIVELES = { '180': [4, 5], '252': [5, 6, 7] };
    var COLOR_LABELS = { gris: 'Galvanizado', anthrazita: 'Gris Anthrazita', bicolor: 'Azul-Naranja' };
    var COLOR_KEYS = { gris: 'v_gris', anthrazita: 'v_anthrazita', bicolor: 'v_bicolor' };
    var TIPO_BALDA_LABELS = { malla: 'Malla galvanizada', madera: 'Tablero de madera' };
    var COLORS = { gris: 0xA8ADB8, anthrazita: 0x383E42, bicolor: 0xD8DCE3 };
    var MATERIAL_PROPS = { gris: { roughness: 0.65, metalness: 0.18 }, anthrazita: { roughness: 0.65, metalness: 0.18 }, bicolor: { roughness: 0.65, metalness: 0.10 } };
    var BICOLOR_ANGULAR = 0x004F7C, BICOLOR_LARGUERO = 0xE25303;
    var WOOD_COLOR = 0xB29272, WOOD_PROPS = { roughness: 0.92, metalness: 0.00 };

    // ─── Carga por balda (kg) — portada 1:1 de configurator.js:129-132 (producción).
    // Valores reales de custom.capacidad_de_carga_por_balda_kg: dependen de ALTURA +
    // TIPO DE BALDA, uniformes en ancho/fondo/niveles (verificado en 240 productos):
    //   180 cm → malla 300 / madera 360   ·   252 cm → 260 (ambos tipos). ──
    var CAP_MAX = 360;
    function loadCapacity(cfg) {
      cfg = cfg || {};
      if ((parseInt(cfg.altura, 10) || 180) >= 252) return 260;
      return (cfg.tipoBalda === 'madera') ? 360 : 300;
    }

    // ─── Altura 252cm sintética (portada 1:1 del configurador de producto,
    // configurator.js:559-757 — probada en producción). Sin GLB de 252, el
    // puntal son 2 tramos apilados: se CLONA el tramo superior desplazado un
    // tramo (el patrón de taladros continúa con su paso real) y se recorta por
    // una zona lisa; los niveles se desplazan EN BLOQUE. NUNCA estirar. ──
    var H252_FACTOR = 2520 / 1840;

    function levelIndexFromName(name) {
      var m = name.match(/^(Bandeja|Malla|Larguero)(4560|45|60)(\d+)$/i);
      if (m) return parseInt(m[3], 10);
      m = name.match(/^Travesano(45|60)(\d+)$/i);
      if (m) return Math.ceil(parseInt(m[2], 10) / 2);
      m = name.match(/^Refuerzo(45|60)(\d+)$/i);
      if (m) { var i = parseInt(m[2], 10); return i > 13 ? i - 13 : i; }
      return null;
    }

    function shiftWorldY(THREE, obj, dy) {
      var ps = new THREE.Vector3(1, 1, 1);
      if (obj.parent) obj.parent.getWorldScale(ps);
      obj.position.y += dy / (ps.y || 1);
      obj.updateMatrixWorld(true);
    }

    // Recorta la geometría del clon por ENCIMA de worldCut, ajustando el corte
    // a la zona lisa más cercana (mayor hueco sin vértices = pared sin taladros).
    // Trabaja detectando qué eje local es el vertical y su signo (los tramos
    // del GLB vienen rotados 180°).
    function cropMeshAboveWorldY(THREE, mesh, worldCut) {
      var g = mesh.geometry.index ? mesh.geometry.toNonIndexed() : mesh.geometry.clone();
      var pos = g.attributes.position;
      mesh.updateMatrixWorld(true);
      var e = mesh.matrixWorld.elements;
      var contrib = [e[1], e[5], e[9]];
      var axis = 0;
      if (Math.abs(contrib[1]) >= Math.abs(contrib[0]) && Math.abs(contrib[1]) >= Math.abs(contrib[2])) axis = 1;
      else if (Math.abs(contrib[2]) >= Math.abs(contrib[0])) axis = 2;
      var s = contrib[axis] || 1, o = e[13];
      var cutVal = (worldCut - o) / s;
      var sign = s > 0 ? 1 : -1;

      function val(idx) { return pos.array[idx * 3 + axis]; }

      var gMin = Infinity, gMax = -Infinity, i, v;
      for (i = 0; i < pos.count; i++) { v = val(i); if (v < gMin) gMin = v; if (v > gMax) gMax = v; }
      var span = gMax - gMin;

      var win = span * 0.08, vs = [];
      for (i = 0; i < pos.count; i++) { v = val(i); if (Math.abs(v - cutVal) <= win) vs.push(v); }
      vs.sort(function (a, b) { return a - b; });
      var bestGap = 0, bestMid = cutVal;
      for (i = 1; i < vs.length; i++) {
        var gap = vs[i] - vs[i - 1];
        if (gap > bestGap) { bestGap = gap; bestMid = (vs[i] + vs[i - 1]) / 2; }
      }
      if (bestGap > span * 0.01) cutVal = bestMid;

      function above(v2) { return sign > 0 ? v2 > cutVal : v2 < cutVal; }

      var triCount = Math.floor(pos.count / 3), keep = [], t;
      for (t = 0; t < triCount; t++) {
        if (above(val(t * 3)) && above(val(t * 3 + 1)) && above(val(t * 3 + 2))) continue;
        keep.push(t);
      }
      var out = new THREE.BufferGeometry();
      Object.keys(g.attributes).forEach(function (key) {
        var src = g.attributes[key], item = src.itemSize;
        var arr = new Float32Array(keep.length * 3 * item);
        for (var k = 0; k < keep.length; k++) {
          for (var w = 0; w < 3; w++) {
            var sI = (keep[k] * 3 + w) * item, dI = (k * 3 + w) * item;
            for (var c = 0; c < item; c++) arr[dI + c] = src.array[sI + c];
          }
        }
        out.setAttribute(key, new THREE.BufferAttribute(arr, item));
      });
      var oArr = out.attributes.position.array;
      for (i = 0; i < out.attributes.position.count; i++) {
        if (above(oArr[i * 3 + axis])) oArr[i * 3 + axis] = cutVal;
      }
      out.attributes.position.needsUpdate = true;
      out.computeBoundingBox(); out.computeBoundingSphere();
      out.userData.unitSpecific = true; // geometría propia (no compartida con la caché): sí se destruye
      return out;
    }

    function apply252(model, THREE) {
      if (model.userData.h252Applied) return;
      model.userData.h252Applied = true;

      var f = H252_FACTOR;
      model.updateMatrixWorld(true);

      // 1. Puntales: tercer tramo clonado con el patrón de taladros intacto
      var angs = [];
      model.traverse(function (c) {
        if (c.isMesh && /^Angular(4560|45|60)\d+$/i.test(c.name || '')) angs.push(c);
      });
      var posts = {};
      angs.forEach(function (m) {
        var box = new THREE.Box3().setFromObject(m);
        if (box.isEmpty()) return;
        var depth = m.name.match(/^Angular(4560|45|60)/i)[1];
        var key = depth + '|' + ((box.min.x + box.max.x) / 2).toFixed(2) + '|' + ((box.min.z + box.max.z) / 2).toFixed(2);
        (posts[key] = posts[key] || []).push({ mesh: m, box: box });
      });

      var oldPostTop = 0, newPostTop = 0;
      Object.keys(posts).forEach(function (key) {
        var segs = posts[key];
        segs.sort(function (a, b) { return a.box.min.y - b.box.min.y; });
        var bottom = segs[0], top = segs[segs.length - 1];
        oldPostTop = Math.max(oldPostTop, top.box.max.y);
        if (segs.length < 2) return;
        var D = top.box.min.y - bottom.box.min.y;
        var targetTop = bottom.box.min.y + (top.box.max.y - bottom.box.min.y) * f;

        var clone = top.mesh.clone();
        clone.name = top.mesh.name + '0'; // sigue cumpliendo ^Angular(4560|45|60)\d+$
        top.mesh.parent.add(clone);
        shiftWorldY(THREE, clone, D);
        clone.geometry = cropMeshAboveWorldY(THREE, clone, targetTop);
        clone.updateMatrixWorld(true);
        var cbox = new THREE.Box3().setFromObject(clone);
        if (!cbox.isEmpty()) newPostTop = Math.max(newPostTop, cbox.max.y);
      });

      var EXT = newPostTop - oldPostTop;
      if (EXT <= 0) return;

      // 2. Pletinas de unión: reposicionar proporcionalmente (mitad del poste)
      var postRatio = oldPostTop > 0 ? newPostTop / oldPostTop : f;
      model.traverse(function (c) {
        if (!/^PLETINAS(4560|45|60)$/i.test(c.name || '')) return;
        var b = new THREE.Box3().setFromObject(c);
        if (b.isEmpty()) return;
        var cy = (b.min.y + b.max.y) / 2;
        shiftWorldY(THREE, c, cy * (postRatio - 1));
      });

      // 3. Niveles: desplazamiento uniforme por nivel (el superior conserva
      // exacta su distancia al extremo del puntal)
      var lvNodes = [];
      model.traverse(function (c) {
        if (levelIndexFromName(c.name || '') != null) lvNodes.push(c);
      });
      lvNodes = lvNodes.filter(function (n) {
        var p = n.parent;
        while (p) { if (lvNodes.indexOf(p) > -1) return false; p = p.parent; }
        return true;
      });
      var levels = {};
      lvNodes.forEach(function (c) {
        var lv = levelIndexFromName(c.name);
        (levels[lv] = levels[lv] || []).push(c);
      });
      var yTopLevels = 0, lvTop = {};
      Object.keys(levels).forEach(function (lv) {
        var topY = -Infinity;
        levels[lv].forEach(function (c) {
          var b = new THREE.Box3().setFromObject(c);
          if (!b.isEmpty() && b.max.y > topY) topY = b.max.y;
        });
        lvTop[lv] = topY;
        if (topY > yTopLevels) yTopLevels = topY;
      });
      if (yTopLevels <= 0) return;
      Object.keys(levels).forEach(function (lv) {
        if (lvTop[lv] === -Infinity) return;
        var delta = (lvTop[lv] / yTopLevels) * EXT;
        levels[lv].forEach(function (c) { shiftWorldY(THREE, c, delta); });
      });
    }

    function applyVisibility(model, cfg) {
      var show = String(cfg.profundidad);
      var n = cfg.niveles;
      var tipo = cfg.tipoBalda || 'malla';
      var maxPos = 13;
      var visiblePos = {};
      for (var i = 0; i < n; i++) visiblePos[(n === 1) ? 1 : Math.round(1 + i * (maxPos - 1) / (n - 1))] = true;

      model.traverse(function (child) {
        var name = child.name || ''; if (!name) return;
        var m;
        if ((m = name.match(/^Bandeja(4560|45|60)(\d+)$/i))) {
          child.visible = (m[1] === show || m[1] === '4560') && !!visiblePos[parseInt(m[2], 10)] && (tipo === 'madera'); return;
        }
        if ((m = name.match(/^Malla(4560|45|60)(\d+)$/i))) {
          child.visible = (m[1] === show || m[1] === '4560') && !!visiblePos[parseInt(m[2], 10)] && (tipo === 'malla'); return;
        }
        if ((m = name.match(/^Larguero(4560|45|60)(\d+)$/i))) {
          child.visible = (m[1] === show || m[1] === '4560') && !!visiblePos[parseInt(m[2], 10)]; return;
        }
        if ((m = name.match(/^Angular(4560|45|60)(\d+)$/i))) { child.visible = (m[1] === show || m[1] === '4560'); return; }
        if ((m = name.match(/^Travesano(45|60)(\d+)$/i))) { child.visible = (m[1] === show) && !!visiblePos[Math.ceil(parseInt(m[2], 10) / 2)]; return; }
        if ((m = name.match(/^Refuerzo(45|60)(\d+)$/i))) {
          var rIdx = parseInt(m[2], 10);
          child.visible = (m[1] === show) && !!visiblePos[rIdx > 13 ? rIdx - 13 : rIdx];
          return;
        }
        if ((m = name.match(/^(PIES|PLETINAS)(4560|45|60)$/i))) child.visible = (m[2] === show || m[2] === '4560');
      });
    }

    function applyColor(model, cfg) {
      var colorKey = cfg.color || 'gris';
      var isMadera = cfg.tipoBalda === 'madera';
      var isBicolor = colorKey === 'bicolor';
      var hexMetal = COLORS[colorKey] || COLORS.gris;
      var propsMetal = MATERIAL_PROPS[colorKey] || MATERIAL_PROPS.gris;

      model.traverse(function (child) {
        if (!child.isMesh || !child.material) return;
        var name = child.name || '';
        if (/^(Refuerzo|PLETINAS)/i.test(name)) {
          var matsRef = Array.isArray(child.material) ? child.material : [child.material];
          matsRef.forEach(function (m) {
            if (m.color) m.color.setHex(COLORS.gris);
            if (m.roughness !== undefined) m.roughness = MATERIAL_PROPS.gris.roughness;
            if (m.metalness !== undefined) m.metalness = MATERIAL_PROPS.gris.metalness;
          });
          return;
        }
        var hex, props;
        if (isBicolor) {
          if (/^Angular/i.test(name)) { hex = BICOLOR_ANGULAR; props = MATERIAL_PROPS.bicolor; }
          else if (/^(Larguero|Travesano)/i.test(name)) { hex = BICOLOR_LARGUERO; props = MATERIAL_PROPS.bicolor; }
          else if (isMadera && /^Bandeja/i.test(name)) { hex = WOOD_COLOR; props = WOOD_PROPS; }
          else { hex = COLORS.gris; props = MATERIAL_PROPS.gris; }
        } else {
          var isBandeja = /^Bandeja/i.test(name);
          hex = (isMadera && isBandeja) ? WOOD_COLOR : hexMetal;
          props = (isMadera && isBandeja) ? WOOD_PROPS : propsMetal;
        }
        var mats = Array.isArray(child.material) ? child.material : [child.material];
        mats.forEach(function (m) {
          if (m.color) m.color.setHex(hex);
          if (props.roughness !== undefined && m.roughness !== undefined) m.roughness = props.roughness;
          if (props.metalness !== undefined && m.metalness !== undefined) m.metalness = props.metalness;
        });
      });
    }

    GAMAS.rivet = {
      id: 'rivet', label: 'RIVET PRO', loadMode: 'scene',
      labelKey: 'gama_rivet_label', useCaseKey: 'gama_rivet_use',
      description: 'Talleres y almacén general, montaje sin tornillos: cargas medias-altas por balda.',
      capacity: function (cfg) { return { value: loadCapacity(cfg), unitKey: 'cap_unit_shelf' }; },
      capacityMax: function () { return { value: CAP_MAX, unitKey: 'cap_unit_shelf' }; },
      fields: [
        { key: 'anchura', label: 'Anchura', unit: 'cm', options: ['90', '120', '150', '180'] },
        { key: 'altura', label: 'Altura', unit: 'cm', options: ['180', '252'] },
        { key: 'profundidad', label: 'Fondo', unit: 'cm', options: ['45', '60'] },
        { key: 'niveles', label: 'Niveles', dynamic: true },
        { key: 'tipoBalda', label: 'Tipo de balda', options: ['malla', 'madera'], labels: TIPO_BALDA_LABELS },
        { key: 'color', label: 'Color', options: ['gris', 'anthrazita', 'bicolor'], labels: COLOR_LABELS, swatch: true, swatchColors: { gris: '#A8ADB8', anthrazita: '#383E42', bicolor: '#004F7C' } }
      ],
      defaultConfig: function () { return { anchura: '120', altura: '180', niveles: 4, profundidad: '45', color: 'gris', tipoBalda: 'malla' }; },
      nivelesFor: function (cfg) { return ALTURA_NIVELES[String(cfg.altura || 180)] || [4, 5]; },
      footprintMM: function (cfg) { return { width: WIDTHS_MM[cfg.anchura] || 900, depth: DEPTHS_MM[cfg.profundidad] || 450, height: (parseInt(cfg.altura, 10) || 180) * 10 }; },
      resolveGlbUrl: function (cfg, urls) {
        var alt = String(cfg.altura || 180);
        var url = urls && urls[alt] && urls[alt][String(cfg.anchura)];
        if (url) return url;
        // 252 sin GLB propio: cargar el de 180 — postProcess lo extiende a 2520mm
        // clonando el tramo superior del puntal (nunca estirando).
        return urls && urls['180'] && urls['180'][String(cfg.anchura)];
      },
      applyVisibility: applyVisibility,
      applyColor: applyColor,
      postProcess: function (model, cfg, THREE) {
        if (parseInt(cfg.altura, 10) === 252) apply252(model, THREE);
      },
      buildHandle: function (cfg) {
        var H = String(cfg.altura || 180), W = String(cfg.anchura), D = String(cfg.profundidad), N = String(cfg.niveles);
        var balda = cfg.tipoBalda || 'malla';
        var typeSlug = balda === 'madera' ? 'con-tablero-aglomerado' : 'con-niveles-de-malla-metalica';
        var typeCode = balda === 'madera' ? 'drtpro1' : 'drmpro1';
        var base = 'estanteria-metalica-para-carga-pesada-rivet-pro-' + typeSlug + '-' + H + 'x' + W + 'x' + D + 'cm-' + N + '-niveles';
        if (cfg.color === 'anthrazita') return base;
        var W3 = p3(W), D3 = p3(D);
        var colorSfx = (cfg.color === 'bicolor') ? 'jg' : 'z';
        return base + '-' + typeCode + '-' + N + H + W3 + D3 + colorSfx;
      },
      deriveSku: function (cfg) {
        var niv = parseInt(cfg.niveles, 10); if (!niv) return null;
        var baldaPrefix = (cfg.tipoBalda === 'madera') ? 'DRT' : 'DRM';
        var colorCode = (cfg.color === 'anthrazita') ? 'D8' : (cfg.color === 'bicolor') ? 'JG' : 'Z';
        return baldaPrefix + 'PRO1-' + niv + '/' + p3(cfg.altura || 180) + p3(cfg.anchura) + p3(cfg.profundidad) + colorCode;
      },
      searchQueries: function (cfg) {
        var dims = (cfg.altura || 180) + 'x' + cfg.anchura + 'x' + cfg.profundidad;
        var niv = parseInt(cfg.niveles, 10);
        return ['rivet pro ' + dims + ' ' + niv, 'rivet pro ' + dims, dims + ' ' + niv, dims];
      },
      summary: function (cfg, tr) {
        return 'RIVET PRO ' + fmtDim(WIDTHS_MM[cfg.anchura]) + '×' + fmtDim(DEPTHS_MM[cfg.profundidad]) + '×' + cfg.altura + 'cm · ' +
          cfg.niveles + ' ' + tr_(tr, 'niveles_word', 'niveles') + ' · ' +
          tr_(tr, COLOR_KEYS[cfg.color], COLOR_LABELS[cfg.color] || cfg.color) +
          (cfg.tipoBalda === 'madera' ? ' · ' + tr_(tr, 'v_madera', TIPO_BALDA_LABELS.madera) : '');
      }
    };
  })();

  // ════════════════════════════════════════════════════════════════════════
  // BULLRACK RIVET PRO — un GLB por anchura, visibilidad por nodo.
  // Lógica portada de shopify/assets/configurator-bullrack.js (producción, verificada
  // sobre los 96 productos reales de la gama). Los GLB los entregó el modelador con
  // TRES decisiones distintas a las de RIVET, del que se clonó ese configurador, y las
  // tres se resuelven aquí:
  //   1. EJES GIRADOS 90°: el GLB modela X=FONDO y Z=ANCHO → se rota -90° sobre Y
  //      (ver orientAxes; va en postProcess y en un pivote INTERNO, no en el root).
  //   2. 5 POSICIONES DE BALDA modeladas (una por nivel), no 13 ranuras fijas como
  //      RIVET → se muestran los índices 1..N y se REPOSICIONAN (con 4 niveles el
  //      espaciado real del producto es más ANCHO: ocultar no basta).
  //   3. REFUERZOS 3 POR NIVEL (15 en total), no 2 → nivel = ceil(idx/3). La fórmula
  //      heredada de RIVET (idx>13 ? idx-13 : idx) repartía los 3 refuerzos del nivel 1
  //      a los niveles 1, 2 y 3.
  // ════════════════════════════════════════════════════════════════════════
  (function () {
    var WIDTHS_MM = { '120': 1200, '150': 1500, '180': 1800, '200': 2000 };
    var DEPTHS_MM = { '45': 450, '60': 600 };
    var HEIGHT_MM = 2000;                          // altura FIJA de la gama (200 cm en los 96 productos)
    var ALTURA_NIVELES = { '200': [4, 5] };        // misma forma que RIVET aunque aquí solo haya una altura
    var COLOR_LABELS = { gris: 'Galvanizado', anthrazita: 'Gris Anthrazita', bicolor: 'Azul-Naranja' };
    var COLOR_KEYS = { gris: 'v_gris', anthrazita: 'v_anthrazita', bicolor: 'v_bicolor' };
    var TIPO_BALDA_LABELS = { malla: 'Malla galvanizada', madera: 'Tablero de madera' };
    var COLORS = { gris: 0xA8ADB8, anthrazita: 0x383E42, bicolor: 0xD8DCE3 };
    // ⚠️ Las claves de MATERIAL_PROPS deben coincidir con las de COLORS y con los valores
    // de cfg.color. En producción hubo un fallo SILENCIOSO justo por esto: la tabla
    // heredó la clave 'negro' de RIVET en vez del acabado real, así que ese color caía al
    // fallback y se pintaba con las propiedades del galvanizado sin que nada lo delatase.
    var MATERIAL_PROPS = {
      gris:       { roughness: 0.65, metalness: 0.18 },  // galvanizado
      anthrazita: { roughness: 0.60, metalness: 0.12 },  // pintura en polvo RAL 7016
      bicolor:    { roughness: 0.65, metalness: 0.10 }
    };
    var BICOLOR_ANGULAR = 0x004F7C, BICOLOR_LARGUERO = 0xE25303; // azul RAL 5010 · naranja RAL 2004
    var WOOD_COLOR = 0xB29272, WOOD_PROPS = { roughness: 0.92, metalness: 0.00 };

    // ─── Carga por balda (kg) — portada 1:1 de configurator-bullrack.js:135-139
    // (producción). Valores reales de custom.capacidad_de_carga_por_balda_kg,
    // verificados en los 96 productos: tablero aglomerado 480 con 4 niveles / 380 con 5;
    // malla galvanizada 300 con ambos. Aquí depende de NIVELES + tipo de balda (en RIVET
    // dependía de la altura). ──
    var CAP_MAX = 480;
    function loadCapacity(cfg) {
      cfg = cfg || {};
      var niv = parseInt(cfg.niveles, 10) || 4;
      if (cfg.tipoBalda === 'madera') return (niv >= 5) ? 380 : 480;
      return 300;
    }

    // URLs de respaldo si la sección no las cablea: las MISMAS que los defaults del schema
    // de sections/product-configurator-bullrack.liquid (verificadas en producción), un GLB
    // por anchura. Son las de `originalSource` de Media 3D (ruta SIN /o/), que sirven los
    // bytes tal cual con los NOMBRES DE NODO intactos — de ellos depende toda la
    // visibilidad. Las de `sources[]` (con /o/) están optimizadas para el visor AR de
    // Shopify y BORRAN los nombres (91 → 0) sin dar ningún error: el visor queda
    // inservible en silencio.
    var FALLBACK_GLB = {
      '120': 'https://cdn.shopify.com/3d/models/6bd967d537b986c3/BullRackRivet200x120x60-web.glb',
      '150': 'https://cdn.shopify.com/3d/models/47734b66e43fd76c/BullRackRivet200x150x60-web.glb',
      '180': 'https://cdn.shopify.com/3d/models/53ed5b5466a547f1/BullRackRivet200x180x60-web.glb',
      '200': 'https://cdn.shopify.com/3d/models/9797a034a7401602/BullRackRivet200x200x60-web.glb'
    };

    // Nivel (1..5) al que pertenece una pieza según su nombre, o null. BULLRACK modela 1
    // posición POR NIVEL: Bandeja/Malla/Larguero llevan 1 pieza por nivel (índice =
    // nivel), Travesano 2 (idx/2) y Refuerzo 3 (idx 1-15 → ceil(idx/3)).
    function levelIndexFromName(name) {
      var m = name.match(/^(Bandeja|Malla|Larguero)(4560|45|60)(\d+)$/i);
      if (m) return parseInt(m[3], 10);
      m = name.match(/^Travesano(45|60)(\d+)$/i);
      if (m) return Math.ceil(parseInt(m[2], 10) / 2);
      m = name.match(/^Refuerzo(45|60)(\d+)$/i);
      if (m) return Math.ceil(parseInt(m[2], 10) / 3);
      return null;
    }

    function shiftWorldY(THREE, obj, dy) {
      var ps = new THREE.Vector3(1, 1, 1);
      if (obj.parent) obj.parent.getWorldScale(ps);
      obj.position.y += dy / (ps.y || 1);
      obj.updateMatrixWorld(true);
    }

    // ─── Refuerzo45 sintético para la anchura 200 (hueco real del entregable) ──────
    // El GLB de ancho 200 no trae NINGÚN nodo Refuerzo45 (0 de 15); los otros tres anchos
    // sí. Se clona el Refuerzo60 equivalente en runtime — mismo patrón que la síntesis de
    // baldas/postes de sin-tornillos — en vez de dejar el hueco hasta un reexport de
    // Blender. Es *self-healing*: si el GLB incorpora el Refuerzo45 real algún día, el
    // nombre exacto ya existe y esta función no actúa.
    //
    // Delta medido en el eje LOCAL X del nodo (el eje del FONDO en el GLB sin rotar) entre
    // Refuerzo45<i> y Refuerzo60<i> en los archivos que traen ambos: -0.15466 m en el de
    // 120 Y en el de 150 (idéntico en dos archivos independientes; el de 180 da -0.14413,
    // ~1 cm de variación de modelado). Se usa el valor que coincide en dos archivos.
    var REFUERZO_45_60_DX = -0.15466;
    // ⚠️ NO BASTA CON REPOSICIONAR: el refuerzo de fondo 45 es una pieza FÍSICAMENTE MÁS
    // CORTA. Los dos nodos COMPARTEN la misma malla y lo que los diferencia, además de la
    // posición, es la ESCALA del nodo: scale.y 0.000753 (45) frente a 0.001003 (60), o sea
    // 45/60 en su eje LOCAL Y, que tras la rotación del nodo es la dirección del FONDO.
    // Sin esta escala el clon mide 58 cm de fondo en vez de 45, sobresale ~14 cm por
    // detrás del bastidor e infla el bbox a 0.749 m en vez de 0.605 → descuadra el
    // escalado/encuadre y la huella en planta. El eje a escalar se MIDE, no se deduce de
    // la rotación del nodo (mismo gotcha que en sin-tornillos).
    var REFUERZO_45_60_SCALE_Y = 45 / 60;

    function synthesizeRefuerzo45(model) {
      // 1) Limpiar los clones de una pasada anterior antes de reevaluar (no acumular).
      //    Hoy el motor recarga un clon limpio en cada cambio de config
      //    (loadAndPlaceItem → loadCachedScene), pero esto lo hace idempotente igual.
      var stale = [];
      model.traverse(function (c) { if (c.userData && c.userData.bSynth) stale.push(c); });
      stale.forEach(function (c) { if (c.parent) c.parent.remove(c); });

      // 2) Si el GLB ya trae Refuerzo45 real, nada que sintetizar (self-healing)
      var has45 = false;
      model.traverse(function (c) { if (/^Refuerzo45\d+$/i.test(c.name || '')) has45 = true; });
      if (has45) return;

      // 3) Clonar cada Refuerzo60<i> real: desplazarlo el delta medido en X Y ACORTARLO.
      //    Se trabaja en el frame LOCAL del nodo, que la corrección de ejes de
      //    orientAxes() no altera (solo inserta un ancestro rotado).
      var srcs = [];
      model.traverse(function (c) { if (/^Refuerzo60(\d+)$/i.test(c.name || '')) srcs.push(c); });
      srcs.forEach(function (c) {
        var m = /^Refuerzo60(\d+)$/i.exec(c.name);
        var clone = c.clone();
        clone.name = 'Refuerzo45' + m[1];
        clone.position.x += REFUERZO_45_60_DX;
        clone.scale.y *= REFUERZO_45_60_SCALE_Y;   // sin esto el clon mide 60cm de fondo
        // Propaga el desplazamiento de nivel ya aplicado (si lo hubiera): sin esto, la Y
        // "actual" del clon se confundiría con su Y "natural" en la siguiente pasada de
        // repositionLevels y desviaría el resto de niveles.
        clone.userData = { bSynth: true, vLvShift: (c.userData && c.userData.vLvShift) || 0 };
        if (c.parent) c.parent.add(clone);
      });
    }

    // ─── Reparto de los niveles 1..N sobre las 5 posiciones modeladas ─────────────
    // Con N=5 las piezas ya están en su sitio (no-op). Con N=4 el espaciado real del
    // producto es MÁS ANCHO, así que las piezas de los índices 1..4 se desplazan en Y de
    // MUNDO al reparto uniforme entre el nivel 1 (base fija) y el nivel 5 (tope fijo del
    // bastidor) — mismo patrón que el reparto de baldas de configurator-sintornillos.js.
    // El desplazamiento se calcula SIEMPRE contra la Y NATURAL del GLB (Y actual menos lo
    // ya aplicado, cacheado en userData.vLvShift), nunca de forma incremental sobre donde
    // quedó la pasada previa: si no, alternar 4↔5 niveles arrastraría las piezas.
    var MAX_LEVEL_SLOTS = 5;

    // Desplaza todas las piezas de un nivel a la cota pedida, midiendo siempre contra su
    // Y natural (de ahí el descuento de lo ya aplicado, no un incremento).
    function applyShift(nodes, desired, THREE) {
      nodes.forEach(function (c) {
        var applied = c.userData.vLvShift || 0;
        var delta = desired - applied;
        if (Math.abs(delta) > 1e-6) shiftWorldY(THREE, c, delta);
        c.userData.vLvShift = desired;
      });
    }

    function repositionLevels(model, n, THREE) {
      var byLevel = {};
      model.traverse(function (c) {
        var lv = levelIndexFromName(c.name || '');
        if (lv != null) (byLevel[lv] = byLevel[lv] || []).push(c);
      });
      // Descartar nodos anidados bajo otro nodo ya indexado (evita doble desplazamiento)
      var flat = [];
      Object.keys(byLevel).forEach(function (lv) { flat = flat.concat(byLevel[lv]); });
      Object.keys(byLevel).forEach(function (lv) {
        byLevel[lv] = byLevel[lv].filter(function (nd) {
          var p = nd.parent;
          while (p) { if (flat.indexOf(p) > -1) return false; p = p.parent; }
          return true;
        });
      });

      function naturalY(c) {
        var b = new THREE.Box3().setFromObject(c);
        if (b.isEmpty()) return null;
        return b.max.y - (c.userData.vLvShift || 0);
      }
      function levelNaturalY(lv) {
        var arr = byLevel[lv]; if (!arr || !arr.length) return null;
        var top = -Infinity;
        arr.forEach(function (c) { var y = naturalY(c); if (y != null && y > top) top = y; });
        return top === -Infinity ? null : top;
      }

      var baseY = levelNaturalY(1);
      var topY = levelNaturalY(MAX_LEVEL_SLOTS);

      for (var lv = 1; lv <= MAX_LEVEL_SLOTS; lv++) {
        var arr = byLevel[lv]; if (!arr) continue;
        var lvNatY = levelNaturalY(lv);
        var desired = 0; // por defecto: posición real del GLB, sin desviar
        if (n < MAX_LEVEL_SLOTS && lv <= n && baseY != null && topY != null && lvNatY != null) {
          desired = (baseY + (topY - baseY) * (lv - 1) / (n - 1 || 1)) - lvNatY;
        }
        applyShift(arr, desired, THREE);
      }
    }

    // ─── Corrección de ejes del GLB (90° sobre Y) ─────────────────────────────────
    // Los GLB de BULLRACK vienen modelados con X=FONDO y Z=ANCHO, pero TODO el resto del
    // proyecto asume X=ancho, Z=fondo (footprintMM, huella/solapes en planta, cotas,
    // rotación de ítems). Rotar el modelo -90° sobre Y lo alinea y nada más se entera.
    // El SIGNO se midió, no se dedujo: los postes `Angular` crecen del fondo al frente en
    // +X local, y -π/2 (x'=-z, z'=x) manda el frente a +Z, que es la convención del
    // proyecto (cara trasera en z=0, el fondo crece hacia +Z). Contraste que lo demuestra:
    // un GLB de RIVET que funciona mide X=1.2120 (=ancho 120) y Z=0.6049 (=fondo 45); los
    // de BULLRACK miden X=0.6063 y Z=1.2031-2.0229, o sea intercambiados.
    //
    // ⚠️ La rotación NO puede ir en el root del modelo: finishPlaceItem() del motor hace
    // `rawModel.rotation.set(0,0,0)` justo antes de medir el bbox y se la comería sin
    // dejar rastro. Va en un PIVOTE INTERNO que envuelve a los hijos — el motor resetea
    // el root, no el pivote. (Y como el giro es sobre Y, no afecta a ninguna cota Y: el
    // reparto de niveles da el mismo resultado antes o después de rotar.)
    var BULL_ROTATION_Y = -Math.PI / 2;

    function orientAxes(model, THREE) {
      if (model.userData.bullAxisFixed) return; // idempotente: nunca rotar dos veces
      var kids = model.children.slice();
      var pivot = new THREE.Group();
      pivot.name = 'BullrackAxisFix';
      pivot.rotation.y = BULL_ROTATION_Y;
      for (var i = 0; i < kids.length; i++) pivot.add(kids[i]); // add() reparenta
      model.add(pivot);
      model.userData.bullAxisFixed = true;
      model.updateMatrixWorld(true);
    }

    function applyVisibility(model, cfg) {
      var show = String(cfg.profundidad);
      var n = parseInt(cfg.niveles, 10) || 4;
      var tipo = cfg.tipoBalda || 'malla';

      // Hueco real del GLB de ancho 200 — sintetizar ANTES de fijar visibilidad para que
      // la pieza clonada quede sujeta a la MISMA regex de fondo/nivel que la real.
      synthesizeRefuerzo45(model);

      // 1 posición modelada POR NIVEL (índices 1..5): se muestran los índices 1..n
      // CONTIGUOS, no un reparto disperso sobre 13 ranuras como en RIVET.
      var visiblePos = {};
      for (var i = 1; i <= n; i++) visiblePos[i] = true;

      model.traverse(function (child) {
        var name = child.name || ''; if (!name) return;
        var m;
        // Bandeja = tablero de madera · Malla = malla galvanizada (excluyentes)
        if ((m = name.match(/^Bandeja(4560|45|60)(\d+)$/i))) {
          child.visible = (m[1] === show || m[1] === '4560') && !!visiblePos[parseInt(m[2], 10)] && (tipo === 'madera'); return;
        }
        if ((m = name.match(/^Malla(4560|45|60)(\d+)$/i))) {
          child.visible = (m[1] === show || m[1] === '4560') && !!visiblePos[parseInt(m[2], 10)] && (tipo === 'malla'); return;
        }
        if ((m = name.match(/^Larguero(4560|45|60)(\d+)$/i))) {
          child.visible = (m[1] === show || m[1] === '4560') && !!visiblePos[parseInt(m[2], 10)]; return;
        }
        // Angular (puntales): sin índice de nivel — solo se filtran por fondo
        if ((m = name.match(/^Angular(4560|45|60)(\d+)$/i))) { child.visible = (m[1] === show || m[1] === '4560'); return; }
        if ((m = name.match(/^Travesano(45|60)(\d+)$/i))) { child.visible = (m[1] === show) && !!visiblePos[Math.ceil(parseInt(m[2], 10) / 2)]; return; }
        // Refuerzo: 3 piezas por nivel (los clones sintéticos casan con esta misma regex)
        if ((m = name.match(/^Refuerzo(45|60)(\d+)$/i))) {
          child.visible = (m[1] === show) && !!visiblePos[Math.ceil(parseInt(m[2], 10) / 3)]; return;
        }
        if ((m = name.match(/^(PIES|PLETINAS)(4560|45|60)$/i))) child.visible = (m[2] === show || m[2] === '4560');
      });
    }

    // Normaliza un material del GLB antes de pintarlo. Estos renders vienen con el set PBR
    // completo del modelador y dos ajustes que rompen el visor (síntomas reales de
    // producción: "las baldas parecen cristales" + "el selector de color no funciona"):
    //   1) los mapas (map/metalnessMap/roughnessMap/normalMap/specular…) MULTIPLICAN el
    //      color programático y, siendo la textura base una pintura oscura, el resultado
    //      es NEGRO en los 3 acabados → el selector parece muerto.
    //   2) las baldas (material "Chip Board") traen depthWrite:false → no escriben en el
    //      z-buffer, así que los Refuerzo que hay DEBAJO se pintan encima (efecto cristal).
    //      NO era transparencia: transparent:false, opacity:1, alphaTest:0.
    // ⚠️ A propósito NO se tocan transparent/opacity: son del efecto fantasma que el motor
    // aplica a los ítems no seleccionados; forzarlos aquí se lo comería.
    function normalizeMaterial(m) {
      if (m.map                  !== undefined) m.map                  = null;
      if (m.metalnessMap         !== undefined) m.metalnessMap         = null;
      if (m.roughnessMap         !== undefined) m.roughnessMap         = null;
      if (m.normalMap            !== undefined) m.normalMap            = null;
      if (m.specularIntensityMap !== undefined) m.specularIntensityMap = null;
      if (m.specularColorMap     !== undefined) m.specularColorMap     = null;
      if (m.clearcoatMap         !== undefined) m.clearcoatMap         = null;
      if (m.clearcoatNormalMap   !== undefined) m.clearcoatNormalMap   = null;
      if (m.aoMap                !== undefined) m.aoMap                = null;
      if (m.emissiveMap          !== undefined) m.emissiveMap          = null;
      m.depthWrite = true;
      if (m.alphaTest !== undefined) m.alphaTest = 0;
      m.needsUpdate = true;
    }

    function paintMaterial(m, hex, props) {
      normalizeMaterial(m);
      if (m.color) m.color.setHex(hex);
      if (props.roughness !== undefined && m.roughness !== undefined) m.roughness = props.roughness;
      if (props.metalness !== undefined && m.metalness !== undefined) m.metalness = props.metalness;
    }

    function applyColor(model, cfg) {
      var colorKey = cfg.color || 'gris';
      var isMadera = cfg.tipoBalda === 'madera';
      var isBicolor = colorKey === 'bicolor';
      var hexMetal = COLORS[colorKey] || COLORS.gris;
      var propsMetal = MATERIAL_PROPS[colorKey] || MATERIAL_PROPS.gris;

      model.traverse(function (child) {
        if (!child.isMesh || !child.material) return;
        var name = child.name || '';

        // Refuerzo y PLETINAS: SIEMPRE galvanizados, sea cual sea el color elegido
        // (acero galvanizado en el producto real). normalizeMaterial también aquí.
        if (/^(Refuerzo|PLETINAS)/i.test(name)) {
          var matsRef = Array.isArray(child.material) ? child.material : [child.material];
          matsRef.forEach(function (m) { paintMaterial(m, COLORS.gris, MATERIAL_PROPS.gris); });
          return;
        }

        var hex, props;
        if (isBicolor) {
          // Bicolor: puntales azules · todo lo horizontal naranja · resto galvanizado
          if (/^Angular/i.test(name)) { hex = BICOLOR_ANGULAR; props = MATERIAL_PROPS.bicolor; }
          else if (/^(Larguero|Travesano)/i.test(name)) { hex = BICOLOR_LARGUERO; props = MATERIAL_PROPS.bicolor; }
          else if (isMadera && /^Bandeja/i.test(name)) { hex = WOOD_COLOR; props = WOOD_PROPS; }
          else { hex = COLORS.gris; props = MATERIAL_PROPS.gris; }
        } else {
          var isBandeja = /^Bandeja/i.test(name);
          hex = (isMadera && isBandeja) ? WOOD_COLOR : hexMetal;
          props = (isMadera && isBandeja) ? WOOD_PROPS : propsMetal;
        }
        var mats = Array.isArray(child.material) ? child.material : [child.material];
        mats.forEach(function (m) { paintMaterial(m, hex, props); });
      });
    }

    GAMAS.bullrack = {
      id: 'bullrack', label: 'BULLRACK RIVET PRO', loadMode: 'scene',
      labelKey: 'gama_bullrack_label', useCaseKey: 'gama_bullrack_use',
      description: 'Talleres industriales y zonas de producción: la mayor carga por balda y anchos de 2 m.',
      capacity: function (cfg) { return { value: loadCapacity(cfg), unitKey: 'cap_unit_shelf' }; },
      capacityMax: function () { return { value: CAP_MAX, unitKey: 'cap_unit_shelf' }; },
      fields: [
        { key: 'anchura', label: 'Anchura', unit: 'cm', options: ['120', '150', '180', '200'] },
        { key: 'altura', label: 'Altura', unit: 'cm', options: ['200'] },
        { key: 'profundidad', label: 'Fondo', unit: 'cm', options: ['45', '60'] },
        { key: 'niveles', label: 'Niveles', dynamic: true },
        { key: 'tipoBalda', label: 'Tipo de balda', options: ['malla', 'madera'], labels: TIPO_BALDA_LABELS },
        { key: 'color', label: 'Color', options: ['gris', 'anthrazita', 'bicolor'], labels: COLOR_LABELS, swatch: true, swatchColors: { gris: '#A8ADB8', anthrazita: '#383E42', bicolor: '#004F7C' } }
      ],
      defaultConfig: function () { return { anchura: '120', altura: '200', niveles: 4, profundidad: '45', color: 'gris', tipoBalda: 'malla' }; },
      nivelesFor: function (cfg) { return ALTURA_NIVELES[String(cfg.altura || 200)] || [4, 5]; },
      footprintMM: function (cfg) { return { width: WIDTHS_MM[cfg.anchura] || 1200, depth: DEPTHS_MM[cfg.profundidad] || 450, height: HEIGHT_MM }; },
      resolveGlbUrl: function (cfg, urls) {
        var w = String(cfg.anchura);
        // Un modelo por ANCHURA (los 4 traen el fondo 60 y el 45 como subconjunto de nodos)
        return (urls && urls[w]) || FALLBACK_GLB[w] || FALLBACK_GLB['120'];
      },
      applyVisibility: applyVisibility,
      applyColor: applyColor,
      postProcess: function (model, cfg, THREE) {
        // El motor invoca postProcess tras applyVisibility/applyColor y ANTES de medir el
        // bbox y escalar, que es justo lo que necesitan las dos correcciones de esta gama.
        orientAxes(model, THREE);
        repositionLevels(model, parseInt(cfg.niveles, 10) || 4, THREE);
      },
      // Verificado 96/96 contra los handles reales de shopify/scripts/output/bullrack-all-raw.json
      buildHandle: function (cfg) {
        var H = String(cfg.altura || 200), W = String(cfg.anchura), D = String(cfg.profundidad), N = String(cfg.niveles);
        var balda = cfg.tipoBalda || 'malla';
        // BULLRACK usa 'con-malla-galvanizada' donde RIVET usa 'con-niveles-de-malla-metalica'
        var typeSlug = (balda === 'madera') ? 'con-tablero-aglomerado' : 'con-malla-galvanizada';
        var base = 'estanteria-metalica-para-carga-pesada-bullrack-rivet-pro-' +
          typeSlug + '-' + H + 'x' + W + 'x' + D + 'cm-' + N + '-niveles';
        // Los 96 handles terminan en la PALABRA del color (no en el código de SKU)
        var colorWord = (cfg.color === 'anthrazita') ? 'gris' : (cfg.color === 'bicolor') ? 'azul-naranja' : 'galvanizado';
        return base + '-' + colorWord;
      },
      // D{BT|BM}PRO1-{niveles}/{altura}{anchura}{fondo}{color} — DBT=tablero · DBM=malla
      deriveSku: function (cfg) {
        var niv = parseInt(cfg.niveles, 10); if (!niv) return null;
        var baldaPrefix = (cfg.tipoBalda === 'madera') ? 'DBT' : 'DBM';
        var colorCode = (cfg.color === 'anthrazita') ? 'D8' : (cfg.color === 'bicolor') ? 'JG' : 'Z';
        return baldaPrefix + 'PRO1-' + niv + '/' + p3(cfg.altura || 200) + p3(cfg.anchura) + p3(cfg.profundidad) + colorCode;
      },
      // "bullrack" es MARCA (no se traduce) → discrimina en todos los mercados frente a
      // otras gamas que comparten dimensiones y que copaban el top-10 de suggest.json
      // (tope duro de 10 resultados) dejando fuera el producto correcto.
      searchQueries: function (cfg) {
        var dims = (cfg.altura || 200) + 'x' + cfg.anchura + 'x' + cfg.profundidad;
        var niv = parseInt(cfg.niveles, 10);
        return ['bullrack ' + dims + 'cm ' + niv, 'bullrack ' + dims + 'cm', 'bullrack ' + dims, dims + 'cm ' + niv, dims];
      },
      summary: function (cfg, tr) {
        return 'BULLRACK RIVET PRO ' + fmtDim(WIDTHS_MM[cfg.anchura]) + '×' + fmtDim(DEPTHS_MM[cfg.profundidad]) + '×' + (cfg.altura || 200) + 'cm · ' +
          cfg.niveles + ' ' + tr_(tr, 'niveles_word', 'niveles') + ' · ' +
          tr_(tr, COLOR_KEYS[cfg.color], COLOR_LABELS[cfg.color] || cfg.color) +
          (cfg.tipoBalda === 'madera' ? ' · ' + tr_(tr, 'v_madera', TIPO_BALDA_LABELS.madera) : '');
      }
    };
  })();

  // ════════════════════════════════════════════════════════════════════════
  // VERSATILE (DMO1) — GLB único-catálogo, visibilidad por subconjunto + clon de balda
  // ════════════════════════════════════════════════════════════════════════
  (function () {
    var HEIGHTS_MM = { '150': 1500, '200': 2000, '250': 2500 };
    var WIDTHS_MM = { '60': 600, '80': 800, '90': 900, '100': 1000 };
    var DEPTHS_MM = { '30': 300, '40': 400, '50': 500 };
    var ALTURA_NIVELES = { '150': [4], '200': [4, 5, 6], '250': [4, 5, 6] };
    var COLORS = { galvanizado: 0xA8ADB8 };
    var MATERIAL_PROPS = { galvanizado: { roughness: 0.62, metalness: 0.22 } };

    // ─── Carga por balda (kg) — portada 1:1 de configurator-versatile.js:112
    // (producción). Valor real de custom.capacidad_de_carga_por_balda_kg: 100 kg en
    // TODA la gama (verificado en 96 productos), NO depende del fondo. ──
    var LOAD_CAPACITY = { '30': 100, '40': 100, '50': 100 };
    var CAP_MAX = 100;
    function loadCapacity(cfg) {
      cfg = cfg || {};
      return LOAD_CAPACITY[String(cfg.profundidad)] || CAP_MAX;
    }

    function visibleSet(W, D) {
      var s = {};
      function a(n) { s[n] = 1; }
      a('Angular2001'); a('pie1'); a('tornillos1');
      a('Angular200' + W + '2'); a('pie' + W + '2'); a('tornillos' + W + '2');
      a('Angular20060x' + D + '3'); a('pie' + D + '3');
      a('tornillos60x' + D + '3'); a('tornillos60x' + D + '001');
      a('Angular200' + W + 'x' + D + '4'); a('pie' + W + 'x' + D + '4'); a('tornillos' + W + 'x' + D + '4');
      return s;
    }

    function applyVisibility(model, cfg) {
      var W = String(cfg.anchura), D = String(cfg.profundidad);
      var N = parseInt(cfg.niveles, 10) || 4;
      var shelfName = 'Bandeja' + W + 'x' + D + '1';
      var vis = visibleSet(W, D);

      var stale = [];
      model.traverse(function (c) { if (c.userData && c.userData.vShelfClone) stale.push(c); });
      stale.forEach(function (c) { if (c.parent) c.parent.remove(c); });

      var baseShelf = null;
      model.traverse(function (child) {
        var name = child.name || ''; if (!name) return;
        if (name === shelfName) { child.visible = true; baseShelf = child; return; }
        if (/^Bandeja/i.test(name)) { child.visible = false; return; }
        if (/^(Angular|pie|tornillos|bolts)/i.test(name)) child.visible = !!vis[name];
      });

      if (baseShelf && N > 1) {
        var parent = baseShelf.parent;
        var floorL = Infinity, topL = -Infinity;
        parent.children.forEach(function (c) {
          if (c.visible && /^Angular/i.test(c.name || '') && c.geometry) {
            c.updateMatrix();
            if (!c.geometry.boundingBox) c.geometry.computeBoundingBox();
            var bb = c.geometry.boundingBox.clone().applyMatrix4(c.matrix);
            if (bb.min.y < floorL) floorL = bb.min.y;
            if (bb.max.y > topL) topL = bb.max.y;
          }
        });
        baseShelf.updateMatrix();
        if (!baseShelf.geometry.boundingBox) baseShelf.geometry.computeBoundingBox();
        var sbb = baseShelf.geometry.boundingBox.clone().applyMatrix4(baseShelf.matrix);
        var baseL = (sbb.min.y + sbb.max.y) / 2;
        if (floorL === Infinity) { floorL = baseL - 6; topL = baseL + 194; }
        var topShelfL = topL - (baseL - floorL);
        for (var i = 1; i < N; i++) {
          var tgtL = baseL + (topShelfL - baseL) * i / (N - 1);
          var clone = baseShelf.clone();
          clone.name = shelfName + '_lvl' + i;
          clone.userData = { vShelfClone: true };
          clone.visible = true;
          clone.position.y += (tgtL - baseL);
          parent.add(clone);
        }
      }
    }

    function applyColor(model) {
      var hex = COLORS.galvanizado, props = MATERIAL_PROPS.galvanizado;
      model.traverse(function (child) {
        if (!child.isMesh || !child.material) return;
        var mats = Array.isArray(child.material) ? child.material : [child.material];
        mats.forEach(function (m) {
          if (m.map !== undefined) m.map = null;
          if (m.metalnessMap !== undefined) m.metalnessMap = null;
          if (m.roughnessMap !== undefined) m.roughnessMap = null;
          if (m.normalMap !== undefined) m.normalMap = null;
          if (m.color) m.color.setHex(hex);
          if (m.roughness !== undefined) m.roughness = props.roughness;
          if (m.metalness !== undefined) m.metalness = props.metalness;
          m.needsUpdate = true;
        });
      });
    }

    /* OJO: el id/clave 'versatile' NO cambia nunca — de él dependen las claves de
       priceCache ('gamaId|JSON(cfg)'), los SKU DMO1-…, buildHandle y las URLs de
       compartir ya emitidas. Solo cambia la ETIQUETA visible. */
    GAMAS.versatile = {
      id: 'versatile', label: 'Estantería 100% metal', loadMode: 'scene',
      labelKey: 'gama_versatile_label', useCaseKey: 'gama_versatile_use',
      description: 'Oficinas, tiendas, trasteros y talleres: cajas, herramientas y archivo.',
      capacity: function (cfg) { return { value: loadCapacity(cfg), unitKey: 'cap_unit_shelf' }; },
      capacityMax: function () { return { value: CAP_MAX, unitKey: 'cap_unit_shelf' }; },
      fields: [
        { key: 'anchura', label: 'Anchura', unit: 'cm', options: ['60', '80', '90', '100'] },
        { key: 'altura', label: 'Altura', unit: 'cm', options: ['150', '200', '250'] },
        { key: 'profundidad', label: 'Fondo', unit: 'cm', options: ['30', '40', '50'] },
        { key: 'niveles', label: 'Niveles', dynamic: true }
      ],
      defaultConfig: function () { return { anchura: '90', altura: '200', niveles: 4, profundidad: '40', color: 'galvanizado' }; },
      nivelesFor: function (cfg) { return ALTURA_NIVELES[String(cfg.altura || 200)] || [4, 5, 6]; },
      footprintMM: function (cfg) { return { width: WIDTHS_MM[cfg.anchura] || 900, depth: DEPTHS_MM[cfg.profundidad] || 400, height: HEIGHTS_MM[String(cfg.altura)] || 2000 }; },
      resolveGlbUrl: function (cfg, urls) { return urls && urls.default; },
      applyVisibility: applyVisibility,
      applyColor: applyColor,
      buildHandle: function (cfg) {
        var H = String(cfg.altura || 200), W = String(cfg.anchura), D = String(cfg.profundidad), N = String(cfg.niveles);
        return 'estanteria-metalica-de-angulo-ranurado-' + H + 'x' + W + 'x' + D + 'cm-' + N + '-niveles';
      },
      deriveSku: function (cfg) {
        var niv = parseInt(cfg.niveles, 10); if (!niv) return null;
        return 'DMO1-' + niv + '/' + p3(cfg.altura || 200) + p3(cfg.anchura) + p3(cfg.profundidad) + 'Z';
      },
      searchQueries: function (cfg) {
        var dims = (cfg.altura || 200) + 'x' + cfg.anchura + 'x' + cfg.profundidad;
        var niv = parseInt(cfg.niveles, 10);
        return [dims + 'cm ' + niv, dims + ' ' + niv, dims + 'cm', dims];
      },
      summary: function (cfg, tr) {
        return tr_(tr, 'gama_versatile_label', 'Estantería 100% metal') + ' ' +
          fmtDim(WIDTHS_MM[cfg.anchura]) + '×' + fmtDim(DEPTHS_MM[cfg.profundidad]) + '×' + cfg.altura + 'cm · ' +
          cfg.niveles + ' ' + tr_(tr, 'niveles_word', 'niveles') + ' · ' + tr_(tr, 'v_gris', 'Galvanizado');
      }
    };
  })();

  // ════════════════════════════════════════════════════════════════════════
  // VERSATILE SIN TORNILLOS (DVL1) — GLB único-catálogo, visibilidad por subconjunto,
  // clon de balda por nivel y SÍNTESIS de las piezas que le faltan al modelo.
  // Lógica portada de shopify/assets/configurator-sintornillos.js (producción, gama viva
  // desde el 22-jul-2026). Es la hermana SIN tornillos de `versatile` (DMO1): mismas
  // magnitudes, pero GLB propio con una convención de nodos DISTINTA (AngularST = postes ·
  // Larguero = raíles · Bandeja = baldas, y NINGUNA pieza de tornillo).
  //
  // Cuatro cosas la separan del resto del catálogo:
  //
  //   1. SOLO PERFIL L Y MÓDULO INICIAL, y no es una simplificación: el perfil T
  //      (DVT1/DVT2, 144 productos) perdió el configurador el 28-jul-2026 porque el visor
  //      modela el perfil L, y `DVL2` NO EXISTE (`sku:DVL2*` = 0 productos en la tienda)
  //      ⇒ en perfil L nunca hubo módulo adicional. Por eso esta gama NO declara `chain`
  //      ni `canUseUnitType`: cada ítem colocado es un módulo completo con sus dos
  //      bastidores. Ver defaultConfig.
  //
  //   2. EL GLB TIENE HUECOS DE GEOMETRÍA en combinaciones que SÍ se venden (anchura 120
  //      con cualquier fondo · anchura 100 + fondo 30 · altura 200 + fondo 50): 13 de las
  //      24 combinaciones alto×ancho×fondo. Se SINTETIZAN en runtime clonando la pieza
  //      real más parecida (synthesizeShelf / synthesizePostDepth), en vez de dejar el
  //      bastidor sin balda o sin poste. Es *self-healing*: el día que Blender añada la
  //      pieza real, su nombre exacto pasa a existir y este camino deja de usarse solo.
  //
  //   3. EL HANDLE SE COMPONE POR MERCADO, no en castellano como en las otras cinco: en
  //      esta tienda NO hay mercado ES (los cinco son de·pl·cs·sk·hu, el primario es DE) y
  //      el handle español da 404 en los cinco (medido) ⇒ devolverlo garantizaría un 404 +
  //      búsqueda a CADA visitante. Ver buildHandle.
  //
  //   4. SIN `postProcess`: la síntesis de piezas y el reparto de niveles van DENTRO de
  //      applyVisibility, igual que en el configurador de producto y que en `versatile`.
  //      ⚠️ Y a propósito NO se porta su `centerAndScaleUnit`, que escala en Y de forma NO
  //      uniforme (`base = 2.0/nativeH`, `hF = altura/200`): eso es herencia de DMO1, cuyo
  //      GLB no tiene geometría de 250 cm. Este GLB SÍ modela las dos alturas (postes de
  //      2,0045 y 2,4846 m medidos), así que el escalado uniforme del motor sale bien en
  //      las dos — los dos factores difieren un 0,85 %. Con la fórmula del configurador de
  //      producto, en H=250 el bastidor sale un 19 % más estrecho de lo que le toca.
  // ════════════════════════════════════════════════════════════════════════
  (function () {
    // Cotas NOMINALES en mm (las del catálogo). El bbox real del GLB mide entre 8 y 12 mm
    // más en ancho/fondo (voladizo del bastidor, ~1 %): aquí NO pasa lo de los palés
    // (1100 nominal frente a 1211 de bbox, un 10 %), así que manda el nominal.
    var HEIGHTS_MM = { '200': 2000, '250': 2500 };
    var WIDTHS_MM  = { '60': 600, '80': 800, '100': 1000, '120': 1200 };
    var DEPTHS_MM  = { '30': 300, '40': 400, '50': 500 };

    // Los niveles NO dependen de la altura (misma lista en 200 y en 250), y no hay ningún
    // hueco de surtido que filtrar: la matriz DVL1 es CARTESIANA COMPLETA — 2 alturas × 4
    // anchuras × 3 fondos × 3 niveles = 72 teóricos = 72 productos reales, verificado por
    // dos vías independientes (Admin API `sku:DVL1-*` y los 72 handles resueltos en DE con
    // su SKU exacto). Se conserva la forma de tabla por coherencia con las otras gamas.
    var ALTURA_NIVELES = { '200': [4, 5, 6], '250': [4, 5, 6] };

    // ─── Carga por balda (kg) — portada 1:1 de configurator-sintornillos.js:128
    // (producción). Valor real de custom.capacidad_de_carga_por_balda_kg, indexado por
    // PERFIL (no por profundidad, a diferencia del Versatile con tornillos): perfil L
    // (ángulo ligero) = 180 · perfil T (reforzado) = 250. Como esta gama es SOLO perfil L,
    // la capacidad es CONSTANTE: 180 kg/balda, sin depender de alto, ancho, fondo ni
    // niveles (cruce completo de los 72 productos: 180 en los 72).
    // ⚠️ El "250kg" que aparece en los handles CS y SK es un SLUG OBSOLETO, no la carga:
    // ver la nota de buildHandle. No lo tomes como fuente.
    var LOAD_CAPACITY = 180;

    // ─── Acabado único (galvanizado) ──────────────────────────────────────────────
    // Es PURAMENTE VISUAL: no hay eje de color en Shopify (sufijo de SKU `JZ` único), así
    // que no se declara campo `color` y no afecta a SKU/precio. El hex es AZUL desde el
    // 23-jul-2026 (petición del usuario tras ver una foto de referencia; antes gris
    // 0xA8ADB8), mientras el nombre comercial del acabado sigue siendo "Galvanizado" —
    // exactamente como está en producción en el configurador de producto.
    var COLORS = { galvanizado: 0x1E56B0 };
    var MATERIAL_PROPS = { galvanizado: { roughness: 0.62, metalness: 0.22 } };

    // ─── Nombres de nodo del GLB ──────────────────────────────────────────────────
    // Conjunto de nombres a MOSTRAR para una config (altura H, anchura W, fondo D).
    // 4 postes-esquina: origen · frontal-der por anchura · trasera-izq por fondo ·
    // trasera-der por anchura+fondo. Los índices difieren entre 200 y 250 (así se modeló).
    // ⚠️ three.js 0.134 SANEA los nombres al importar (GLTFLoader → PropertyBinding.
    // sanitizeNodeName ELIMINA '.', ':', '[', ']', '/'), p.ej. el nodo 'AngularST200.80.2'
    // del archivo llega a la escena como 'AngularST200802'. 94 de los 98 nombres de este
    // GLB llevan punto, así que TODOS los nombres de aquí se construyen YA SANEADOS (sin
    // puntos): comparados contra un dump del .glb en crudo parecería que ninguno casa.
    function visibleSet(H, W, D) {
      var s = {}; function a(n) { s[n] = 1; }
      H = String(H); W = String(W); D = String(D);
      // Postes (AngularST{H}...)
      a(H === '250' ? 'AngularST2501' : 'AngularST200');          // origen (frontal-izq, compartido)
      a('AngularST' + H + W + '2');                               // frontal-der (por anchura)
      a('AngularST' + H + D);                                     // trasera-izq (por fondo; 200 no trae fondo 50)
      a(H === '250' ? ('AngularST250' + W + 'x' + D + '4')        // trasera-der (por anchura+fondo)
                    : ('AngularST200' + W + 'x' + D));
      // Raíles (Larguero): específicos de la config (anchura y fondo) + su forma base.
      a('Larguero' + W + W + 'x' + D);   // raíl de anchura específico ('Larguero{W}.{W}x{D}')
      a('Larguero' + D + W + 'x' + D);   // raíl de fondo específico  ('Larguero{D}.{W}x{D}')
      a('Larguero' + W + '1'); a('Larguero' + W);   // raíl de anchura base (dos formas según pieza)
      a('Larguero' + D + '1');                       // raíl de fondo base
      return s;
    }

    function findNodeByName(model, name) {
      var found = null;
      model.traverse(function (c) { if (c.name === name) found = c; });
      return found;
    }

    // ─── Síntesis de las piezas que faltan en el GLB ──────────────────────────────
    // Portada 1:1 del configurador de producto (funciones synthesizeShelf /
    // synthesizePostDepth), donde está verificada con una barrida de las 72 combinaciones:
    // recuento de baldas == niveles y 4 postes en TODOS los casos, sin fuga de nodos al
    // reconfigurar. Combinaciones afectadas (13 de 24, todas SKU que se venden):
    //   Bandeja120x{30,40,50} · Bandeja100x30 · AngularST20050
    // Pendiente conocido y NO sintetizado a propósito: los raíles de fondo
    // `Larguero5060x50` y `Larguero5080x50` (anchura 60 y 80 con fondo 50). Su posición
    // depende de ancho Y fondo a la vez y no se pudo aislar una relación lineal de una
    // sola variable con confianza suficiente; el marco queda estructuralmente legible sin
    // él (4 postes + raíl de anchura + raíles base). Candidato a Blender.

    // Balda que falta para (W,D): la forma de una balda solo depende de su FONDO —
    // verificado con bounding boxes locales. ⚠️ EL EJE QUE HAY QUE ESCALAR SE MIDE, NO SE
    // DEDUCE DE LA ROTACIÓN DEL NODO: el cuaternión de la balda ([0.7071,0.7071,0,~0], 180°
    // sobre (1,1,0)) invita a pensar que el ancho vive en scale.x, pero la geometría local
    // mide 3,20 × 106,28 × 52,95 con scale uniforme −0,007532 ⇒ 106,28 × 0,007532 = 0,8004 m
    // = el ANCHO y 52,95 × 0,007532 = 0,3988 m = el FONDO. O sea: grosor = X, ancho = Y,
    // fondo = Z, y el ancho vive en scale.y. La escala es NEGATIVA; multiplicar por un
    // ratio positivo conserva el signo. Por eso se clona la balda del MISMO fondo con la
    // anchura real más cercana y se reescala Y.
    function synthesizeShelf(model, W, D) {
      var widths = [60, 80, 100, 120];
      var best = null;
      widths.forEach(function (w2) {
        if (w2 === W) return;
        var node = findNodeByName(model, 'Bandeja' + w2 + 'x' + D + '1');
        if (node && (!best || Math.abs(w2 - W) < Math.abs(best.w - W))) best = { w: w2, node: node };
      });
      if (!best) return null;
      var clone = best.node.clone();
      clone.name = 'Bandeja' + W + 'x' + D + '1';
      clone.scale.y *= (W / best.w);
      clone.userData = { vSynth: true };
      best.node.parent.add(clone);
      return clone;
    }

    // Poste trasera-izquierda que falta para (H,D): su forma no depende del fondo, solo su
    // posición en Z (verificado: mismo bounding box local en los fondos que sí existen para
    // esa altura). Con ≥2 fondos reales para esa altura se INTERPOLA la posición exacta;
    // con solo 1 se usa el paso medido (~1 cm de Z por cm de fondo, constante en 200 y 250).
    // Para el único caso real (AngularST20050) hay 2 fondos ⇒ interpola y deja la pieza a
    // 4 mm de donde está el AngularST25050 real.
    function synthesizePostDepth(model, H, D) {
      var depths = [30, 40, 50];
      var pts = [];
      depths.forEach(function (d2) {
        var node = findNodeByName(model, 'AngularST' + H + d2);
        if (node) pts.push({ d: d2, z: node.position.z, node: node });
      });
      if (!pts.length) return null;
      pts.sort(function (a, b) { return Math.abs(a.d - D) - Math.abs(b.d - D); });
      var ref = pts[0];
      var rate = -0.00993; // fondo→Z local, m/cm (medido sobre AngularST250 30/40/50 reales)
      if (pts.length > 1) rate = (pts[1].z - pts[0].z) / (pts[1].d - pts[0].d);
      var clone = ref.node.clone();
      clone.name = 'AngularST' + H + D;
      clone.position.z = ref.z + rate * (D - ref.d);
      clone.userData = { vSynth: true };
      ref.node.parent.add(clone);
      return clone;
    }

    // Un único GLB-catálogo: la visibilidad muestra el subconjunto de piezas de la config
    // (H×W×D), sintetiza lo que el GLB no trae, y CLONA la balda base para los N niveles.
    // El reparto es ESPACIADO UNIFORME entre la balda modelada más baja y una superior con
    // margen simétrico — reproducido sobre el GLB: en H=200 los niveles caen al 7,2 · 35,7 ·
    // 64,3 · 92,8 % de la altura del bastidor con N=4, y en H=250 al 5,8 … 94,2 %, con
    // separación perfectamente uniforme e independiente de anchura y fondo. Aquí NO hay
    // remanente ni tabla medida como en palés: la fórmula analítica es la buena.
    function applyVisibility(model, cfg) {
      var H = String(cfg.altura || 200);
      var W = String(cfg.anchura), D = String(cfg.profundidad);
      var N = parseInt(cfg.niveles, 10) || 4;
      var shelfName = 'Bandeja' + W + 'x' + D + '1';
      var vis = visibleSet(H, W, D);

      // 1) Eliminar clones/síntesis de una pasada anterior (no acumular). Hoy el motor
      //    carga un clon limpio en cada cambio de config (loadAndPlaceItem →
      //    loadCachedScene), pero esto lo hace idempotente igual.
      var stale = [];
      model.traverse(function (c) { if (c.userData && (c.userData.vShelfClone || c.userData.vSynth)) stale.push(c); });
      stale.forEach(function (c) { if (c.parent) c.parent.remove(c); });

      // 2) Visibilidad: solo el subconjunto de la config; balda base visible, resto oculta
      var baseShelf = null;
      var postFound = false;
      model.traverse(function (child) {
        var name = child.name || ''; if (!name) return;
        if (name === shelfName) { child.visible = true; baseShelf = child; return; }
        if (/^Bandeja/i.test(name)) { child.visible = false; return; }
        if (/^(AngularST|Larguero)/i.test(name)) {
          child.visible = !!vis[name];
          if (vis[name] && name === ('AngularST' + H + D)) postFound = true;
        }
      });

      // 2b) Poste trasera-izquierda (por fondo): si esta combinación altura+fondo no trae
      //     geometría propia en el GLB, sintetizarlo.
      if (!postFound) {
        var synthPost = synthesizePostDepth(model, H, D);
        if (synthPost) synthPost.visible = true;
      }

      // 2c) Balda base: si el GLB no trae esta combinación exacta anchura+fondo, sintetizarla.
      if (!baseShelf) {
        baseShelf = synthesizeShelf(model, parseInt(W, 10), D);
        if (baseShelf) baseShelf.visible = true;
      }

      // 3) Clonar la balda base y distribuirla para N niveles. Posicionamiento en espacio
      //    LOCAL del parent — INDEPENDIENTE de la escala del modelo (usar coordenadas de
      //    mundo aquí creaba un bucle con el escalado y lanzaba los clones lejísimos).
      if (baseShelf && N > 1) {
        var parent = baseShelf.parent;
        var floorL = Infinity, topL = -Infinity;
        parent.children.forEach(function (c) {
          if (c.visible && /^Angular/i.test(c.name || '') && c.geometry) {
            c.updateMatrix();
            if (!c.geometry.boundingBox) c.geometry.computeBoundingBox();
            var bb = c.geometry.boundingBox.clone().applyMatrix4(c.matrix);
            if (bb.min.y < floorL) floorL = bb.min.y;
            if (bb.max.y > topL)   topL   = bb.max.y;
          }
        });
        baseShelf.updateMatrix();
        if (!baseShelf.geometry.boundingBox) baseShelf.geometry.computeBoundingBox();
        var sbb   = baseShelf.geometry.boundingBox.clone().applyMatrix4(baseShelf.matrix);
        var baseL = (sbb.min.y + sbb.max.y) / 2;
        // Respaldo INALCANZABLE (los postes visibles siempre traen geometría) y portado
        // literal del configurador de producto. ⚠️ Sus cifras (−6 / +194) están en CM,
        // herencia de DMO1, mientras que este espacio local está en METROS (medido:
        // floorL = 0,000 · topL = 2,005). No lo tomes como referencia de unidades.
        if (floorL === Infinity) { floorL = baseL - 6; topL = baseL + 194; }
        var topShelfL = topL - (baseL - floorL);   // balda superior: margen simétrico arriba
        for (var i = 1; i < N; i++) {
          var tgtL  = baseL + (topShelfL - baseL) * i / (N - 1);
          var clone = baseShelf.clone();
          clone.name = shelfName + '_lvl' + i;
          clone.userData = { vShelfClone: true };
          clone.visible = true;
          clone.position.y += (tgtL - baseL);   // desplazamiento LOCAL, escala-independiente
          parent.add(clone);
        }
      }
    }

    // Acabado único en todas las piezas. Se limpian los mapas PBR del GLB: si se dejan
    // activos MULTIPLICAN el color programático y el resultado es negro (mismo gotcha que
    // en BULLRACK, donde el selector de color "parecía muerto"). A propósito NO se tocan
    // transparent/opacity: son del efecto fantasma que el motor aplica a los ítems no
    // seleccionados, y forzarlos aquí se lo comería.
    function applyColor(model) {
      var hex = COLORS.galvanizado, props = MATERIAL_PROPS.galvanizado;
      model.traverse(function (child) {
        if (!child.isMesh || !child.material) return;
        var mats = Array.isArray(child.material) ? child.material : [child.material];
        mats.forEach(function (m) {
          if (m.map          !== undefined) m.map          = null;
          if (m.metalnessMap !== undefined) m.metalnessMap = null;
          if (m.roughnessMap !== undefined) m.roughnessMap = null;
          if (m.normalMap    !== undefined) m.normalMap    = null;
          if (m.color) m.color.setHex(hex);
          if (m.roughness !== undefined) m.roughness = props.roughness;
          if (m.metalness !== undefined) m.metalness = props.metalness;
          m.needsUpdate = true;
        });
      });
    }

    // ─── Handle POR MERCADO ───────────────────────────────────────────────────────
    // El motor pide shopRoot() + 'products/' + buildHandle(cfg) + '.js' y solo cae a
    // searchQueries si eso falla. Las otras cinco gamas devuelven aquí el handle
    // castellano; en esta NO se puede: la tienda tiene CINCO mercados (de·pl·cs·sk·hu, el
    // primario es DE), no publica hreflang="es" y el handle español da 404 en los cinco
    // (medido, igual que en palés) ⇒ devolverlo garantizaría un 404 + búsqueda a CADA
    // visitante. Pero aquí el handle SÍ es derivable por mercado: los patrones de abajo se
    // midieron del catálogo real y se verificaron exigiendo SKU exacto — 8/8 en cada uno de
    // los cinco mercados (40/40) más la barrida completa 72/72 en DE.
    //
    // ⚠️ TRES trampas, las tres medidas:
    //   1. PL y CS FLEXIONAN el plural de "niveles": pl 4-polki pero 5-polek/6-polek · cs
    //      4-police pero 5-polic/6-polic (regla eslava: 2-4 nominativo, 5+ genitivo). SK NO
    //      flexiona (siempre 'police') y HU tampoco ('szint').
    //   2. CS y SK llevan '250kg' en el handle y hay que ESCRIBIRLO aunque la carga real
    //      sea 180: Shopify no regenera el handle al cambiar el título, así que el slug
    //      quedó congelado de cuando el título decía 250. Si alguien lo "corrige" a 180,
    //      el handle deja de resolver.
    //   3. HU separa las dimensiones con guiones ('200-60-30-cm'), no con 'x', y arrastra
    //      un sufijo largo.
    //
    // Si un patrón falla, el handle simplemente no existe y el motor cae a searchQueries:
    // NUNCA resuelve un producto equivocado, porque todos los tokens del handle son
    // dimensionales o del perfil L (una diferencia de flexión da 404, no otro producto).
    // ⚠️ Esto importa porque el camino del handle del motor NO valida el SKU (a diferencia
    // de resolveLocalBySku): un handle que resolviera a otro producto daría un precio
    // equivocado en silencio.
    var HANDLE_FALLBACK_LOCALE = 'de';   // mercado primario de la tienda
    var HANDLE_BUILDERS = {
      de: function (H, W, D, N) {
        return 'steckregale-aus-metall-l-profil-' + H + 'x' + W + 'x' + D + 'cm-' + N + '-boden-180kg';
      },
      pl: function (H, W, D, N) {
        return 'wtykowy-regal-metalowy-' + H + 'x' + W + 'x' + D + 'cm-profil-l-' + N + '-' + (N <= 4 ? 'polki' : 'polek');
      },
      cs: function (H, W, D, N) {
        return 'bezsroubove-kovove-regaly-' + H + 'x' + W + 'x' + D + 'cm-l-profil-' + N + '-' +
          (N <= 4 ? 'police' : 'polic') + '-250kg-na-polici';
      },
      sk: function (H, W, D, N) {
        return 'bezskrutkove-kovove-regaly-' + H + 'x' + W + 'x' + D + 'cm-l-profil-' + N + '-police-250kg-na-policu';
      },
      hu: function (H, W, D, N) {
        return 'csavarmentes-fem-polcos-allvany-l-profillal-' + H + '-' + W + '-' + D + '-cm-' + N +
          '-szint-fem-polccal-180-kg-polc-kek-horganyzott';
      },
      // ES no es mercado hoy (está sin publicar), pero es el handle CANÓNICO de la gama —
      // el único verificable offline contra el dump del catálogo — y el que serviría si
      // algún día se publica. Cuesta cero tenerlo.
      es: function (H, W, D, N) {
        return 'estanteria-metalica-sin-tornillos-versatile-con-perfil-l-' + H + 'x' + W + 'x' + D + 'cm-' + N + '-niveles';
      }
    };

    function marketLocale() {
      try {
        var l = window.Shopify && window.Shopify.locale;
        return l ? String(l).toLowerCase().slice(0, 2) : '';
      } catch (e) { return ''; }
    }

    GAMAS.sinTornillos = {
      id: 'sinTornillos', label: 'Estantería sin tornillos', loadMode: 'scene',
      labelKey: 'gama_sintornillos_label', useCaseKey: 'gama_sintornillos_use',
      description: 'Trasteros, garajes y almacén ligero: montaje a presión, sin tornillos ni herramientas.',
      // Constante en toda la gama (solo perfil L). Sin `count`/`countUnitKey`: aquí la
      // capacidad se mide en kg por balda, como en las cuatro gamas de estantería.
      capacity: function () { return { value: LOAD_CAPACITY, unitKey: 'cap_unit_shelf' }; },
      capacityMax: function () { return { value: LOAD_CAPACITY, unitKey: 'cap_unit_shelf' }; },
      // Sin campo `color` (acabado único, puramente visual) y SIN campos de perfil ni de
      // tipo de módulo: esta gama solo sirve perfil L / módulo inicial (ver cabecera).
      // Con estos nombres de campo el motor resuelve las etiquetas por su mapa FIELD_I18N
      // (anchura → f_anchura, altura → f_altura, profundidad → f_fondo, niveles →
      // f_niveles) ⇒ CERO claves de locale nuevas para los campos.
      fields: [
        { key: 'anchura', label: 'Anchura', unit: 'cm', options: ['60', '80', '100', '120'] },
        { key: 'altura', label: 'Altura', unit: 'cm', options: ['200', '250'] },
        { key: 'profundidad', label: 'Fondo', unit: 'cm', options: ['30', '40', '50'] },
        { key: 'niveles', label: 'Niveles', dynamic: true }
      ],
      // `perfil` y `tipoModulo` se declaran FIJOS aquí (no son selectores) por la misma
      // razón que en el configurador de producto: el visor modela el perfil L, al perfil T
      // se le quitó el configurador el 28-jul-2026, y el módulo adicional de perfil L no
      // existe (`sku:DVL2*` = 0). Se conservan en la config para que quede explícito qué
      // familia se está pidiendo, pero deriveSku/buildHandle los IGNORAN a propósito: un
      // permalink `?p=` antiguo o manipulado podría traer perfil 't' y resolvería un
      // producto sin configurador. El `color` va igual que en `versatile`, por simetría.
      defaultConfig: function () {
        return { anchura: '80', altura: '200', niveles: 4, profundidad: '40',
                 color: 'galvanizado', perfil: 'l', tipoModulo: 'inicial' };
      },
      // Sin recorte estructural y sin huecos de surtido: la matriz es cartesiana completa
      // (72/72 productos reales). Ninguna lista queda vacía.
      nivelesFor: function (cfg) { return ALTURA_NIVELES[String(cfg.altura || 200)] || [4, 5, 6]; },
      footprintMM: function (cfg) {
        return {
          width: WIDTHS_MM[String(cfg.anchura)] || 800,
          depth: DEPTHS_MM[String(cfg.profundidad)] || 400,
          height: HEIGHTS_MM[String(cfg.altura)] || 2000
        };
      },
      // Un ÚNICO GLB-catálogo para toda la gama (no uno por anchura), igual que
      // `versatile`. Es un ARCHIVO DEL TEMA (assets/versatile-sintornillos.glb, 19,9 MB),
      // no un Media 3D, así que el Liquid lo resuelve con asset_url y no hay URL que
      // configurar. ⚠️ NO cablear aquí la URL de Media 3D que aparece como default en el
      // schema de sections/product-configurator-sintornillos.liquid: es el GLB de DMO1
      // (SISTEMA_VERSATILE.glb, y además con /o/) y no trae NINGÚN nodo AngularST → el
      // visor quedaría vacío sin dar un solo error en consola.
      resolveGlbUrl: function (cfg, urls) { return urls && urls.default; },
      applyVisibility: applyVisibility,
      applyColor: applyColor,
      buildHandle: function (cfg) {
        var H = String(cfg.altura || 200), W = String(cfg.anchura), D = String(cfg.profundidad);
        var N = parseInt(cfg.niveles, 10) || 4;
        var build = HANDLE_BUILDERS[marketLocale()] || HANDLE_BUILDERS[HANDLE_FALLBACK_LOCALE];
        return build(H, W, D, N);
      },
      // DVL1-{niveles}/{altura}{anchura}{fondo}JZ — perfil L · módulo inicial (1) ·
      // JZ = galvanizado (color único). Verificado 72/72 contra los SKU reales del catálogo
      // y contra los 72 handles resueltos en DE. El SKU no se traduce → identifica el
      // producto equivalente en cualquier mercado, y es el único filtro que decide cuál de
      // los candidatos de búsqueda es el bueno.
      deriveSku: function (cfg) {
        var niv = parseInt(cfg.niveles, 10); if (!niv) return null;
        return 'DVL1-' + niv + '/' + p3(cfg.altura || 200) + p3(cfg.anchura) + p3(cfg.profundidad) + 'JZ';
      },
      // ⚠️ El discriminador de gama ("sin tornillos" / "perfil l") es IMPRESCINDIBLE: las
      // medidas de esta gama (200/250 × 60/80/100 × 30/40/50) SOLAPAN con las de DMO1 (la
      // gama `versatile` de este mismo catálogo), y suggest.json tiene un tope DURO de 10
      // resultados ⇒ sin discriminar, el candidato correcto se queda fuera del top-10.
      // El índice de búsqueda es CROSS-LOCALE (medido): una query en castellano ejecutada
      // en cualquiera de los 5 mercados devuelve los productos con su handle YA traducido,
      // así que NO hacen falta queries por mercado. De más selectiva a más laxa; la
      // primera deja el objetivo en la posición #1 en los cinco mercados (72/72 en DE).
      // ⚠️ El sufijo 'cm' es OBLIGATORIO en el token de dimensiones: la query desnuda al
      // estilo de palés ('200x60x30 4') devuelve CERO resultados en los cinco mercados.
      // Las dos últimas queries no llevan discriminador y pueden traer candidatos de DMO1:
      // da igual, el motor valida cada candidato por SKU exacto y los descarta.
      searchQueries: function (cfg) {
        var dims = (cfg.altura || 200) + 'x' + cfg.anchura + 'x' + cfg.profundidad;
        var niv = parseInt(cfg.niveles, 10);
        return [
          'estanteria sin tornillos perfil l ' + dims + 'cm ' + niv,
          'sin tornillos perfil l ' + dims + 'cm',
          'sin tornillos ' + dims + 'cm ' + niv,
          'sin tornillos ' + dims + 'cm',
          dims + 'cm ' + niv,
          dims + 'cm'
        ];
      },
      // Cero castellano visible fuera de los respaldos: 'v_gris' ya vale "Galvanizado" en
      // los 10 idiomas (se reutiliza del namespace configurator.*), y el nombre de la gama
      // sale de su propia clave. El acabado se anuncia como Galvanizado aunque el visor lo
      // pinte azul: es el nombre comercial (sufijo JZ), igual que en la ficha de producto.
      summary: function (cfg, tr) {
        return tr_(tr, 'gama_sintornillos_label', 'Estantería sin tornillos') + ' ' +
          fmtDim(WIDTHS_MM[String(cfg.anchura)] || 800) + '×' +
          fmtDim(DEPTHS_MM[String(cfg.profundidad)] || 400) + '×' + (cfg.altura || 200) + 'cm · ' +
          cfg.niveles + ' ' + tr_(tr, 'niveles_word', 'niveles') + ' · ' +
          tr_(tr, 'v_gris', 'Galvanizado');
      }
    };
  })();

  // ════════════════════════════════════════════════════════════════════════
  // MEDIA CARGA (DLO) — kit de piezas nombradas, ensamblaje analítico
  // ════════════════════════════════════════════════════════════════════════
  (function () {
    var FRAME_HEIGHTS_MM = { '200': 2000, '250': 2500, '300': 3000 };
    var WIDTHS_MM = { '150': 1500, '185': 1850, '225': 2250, '240': 2400 };
    var DEPTHS_MM = { '60': 600, '80': 800, '100': 1000 };
    var ALTO_NIVELES = { '200': [3, 4, 5, 6], '250': [3, 4, 5, 6, 7], '300': [4, 5, 6, 7] };
    var TIPO_LABELS = { metalica: 'Metálica', aglomerado: 'Aglomerado' };
    var TIPO_KEYS = { metalica: 'v_metalica', aglomerado: 'v_aglomerado' };

    // ─── Carga por balda (kg) — portada 1:1 de configurator-media-carga.js:122
    // (producción). Solo depende del ANCHO (verificado vía metafields). ──
    var LOAD_CAPACITY = { '150': 975, '185': 790, '225': 660, '240': 590 };
    var CAP_MAX = 975;
    function loadCapacity(cfg) {
      cfg = cfg || {};
      return LOAD_CAPACITY[String(cfg.ancho)] || CAP_MAX;
    }

    var MC_KIT_LEFT_X = 0.025;
    var MC_Z_OFFSETS = { '60': 0.988, '80': 1.189, '100': 1.389 };
    var MC_FRAME_HEIGHTS_M = { '200': 2.00, '250': 2.44, '300': 3.01 };
    var MC_BALDA_OFFSET_Y = 0.057;
    var MC_BASTIDOR_WIDTH_MM = 111; // ancho físico de un bastidor (0.1112 m medido en el GLB)
    var MODULO_LABELS = { inicial: 'Inicial (bastidores propios)', extension: 'Extensión (comparte bastidor)' };
    var MC_REFUERZOS = {
      '80x150': ['Refuerzo80150x80', 'Refuerzo80150x802'],
      '80x225': ['Refuerzo100225x80', 'Refuerzo100225x802', 'Refuerzo100225x803'],
      '80x240': ['Refuerzo100240x80', 'Refuerzo100240x802', 'Refuerzo100240x803'],
      '100x150': ['Refuerzo100150x100', 'Refuerzo100150x1002'],
      '100x185': ['Refuerzo100185x100', 'Refuerzo100185x1002'],
      '100x225': ['Refuerzo100225x100', 'Refuerzo100225x1002', 'Refuerzo100225x1003'],
      '100x240': ['Refuerzo100240x100', 'Refuerzo100240x1002', 'Refuerzo100240x1003']
    };
    var MC_COLORS = { azul: 0xA8ADB8, naranja: 0xE25303, galva: 0xA8ADB8 };
    var METAL_PROPS = { roughness: 0.65, metalness: 0.18 };
    var RAL_PROPS = { roughness: 0.60, metalness: 0.10 };
    var AGLOMERADO_COLOR = 0xC09060, AGLOMERADO_PROPS = { roughness: 0.92, metalness: 0.00 };

    function leftBastidorName(alto, fondo) {
      return (alto === '200') ? ('Bastidor200.' + fondo + '.1') : ('Bastidor' + alto + '.' + fondo);
    }
    function levelY(idx, total, altoKey) {
      var frameH = MC_FRAME_HEIGHTS_M[altoKey] || 2.00;
      var yMin = 0.148, yMax = frameH - 0.148;
      if (total <= 1) return yMin;
      return yMin + (yMax - yMin) * idx / (total - 1);
    }
    function cloneKitPiece(kitIndex, name, THREE) {
      var key = name.replace(/\./g, '');
      var src = kitIndex[key];
      if (!src) { console.warn('[SpaceCatalog/mediaCarga] Pieza no encontrada en kit:', name); return null; }
      var clone = src.clone(true);
      clone.traverse(function (child) {
        if (child.isMesh && child.material) {
          child.material = Array.isArray(child.material) ? child.material.map(function (m) { return m.clone(); }) : child.material.clone();
        }
      });
      return clone;
    }
    function applyColorToGroup(group, cfg) {
      var isAglomerado = (cfg.tipo === 'aglomerado');
      group.traverse(function (child) {
        if (!child.isMesh || !child.material) return;
        var mats = Array.isArray(child.material) ? child.material : [child.material];
        mats.forEach(function (m) {
          if (!m || !m.color) return;
          var mName = (m.name || '').toLowerCase();
          var hex, props;
          if (mName.indexOf('azul') > -1) { hex = MC_COLORS.azul; props = METAL_PROPS; }
          else if (mName.indexOf('naranja') > -1) { hex = MC_COLORS.naranja; props = RAL_PROPS; }
          else if (mName.indexOf('balda') > -1) { hex = isAglomerado ? AGLOMERADO_COLOR : MC_COLORS.galva; props = isAglomerado ? AGLOMERADO_PROPS : METAL_PROPS; }
          else { hex = MC_COLORS.galva; props = METAL_PROPS; }
          m.color.setHex(hex);
          if (props.roughness !== undefined && m.roughness !== undefined) m.roughness = props.roughness;
          if (props.metalness !== undefined && m.metalness !== undefined) m.metalness = props.metalness;
        });
      });
    }

    GAMAS.mediaCarga = {
      id: 'mediaCarga', label: 'Media Carga (DLO)', loadMode: 'kit',
      labelKey: 'gama_mediacarga_label', useCaseKey: 'gama_mediacarga_use',
      description: 'Picking de carga pesada con módulos largos: almacenes y centros de distribución.',
      capacity: function (cfg) { return { value: loadCapacity(cfg), unitKey: 'cap_unit_shelf' }; },
      capacityMax: function () { return { value: CAP_MAX, unitKey: 'cap_unit_shelf' }; },
      fields: [
        { key: 'ancho', label: 'Anchura', unit: 'cm', options: ['150', '185', '225', '240'] },
        { key: 'alto', label: 'Altura', unit: 'cm', options: ['200', '250', '300'] },
        { key: 'fondo', label: 'Fondo', unit: 'cm', options: ['60', '80', '100'] },
        { key: 'niveles', label: 'Niveles', dynamic: true },
        { key: 'tipo', label: 'Tipo de balda', options: ['metalica', 'aglomerado'], labels: TIPO_LABELS, swatch: true, swatchColors: { metalica: '#A8ADB8', aglomerado: '#C09060' } },
        /* Las claves de este eje se REUTILIZAN del configurador de producto de la gama
           (`configurator.options.tipo_modulo` / `values.tipo_modulo_inicial|adicional`),
           que ya está traducido en los 10 locales: la ficha de Media Carga llama
           "Adicional" a lo que aquí se llamaba "Extensión", así que el visitante ve el
           mismo término en los dos sitios y no hay traducciones nuevas que subir. */
        { key: 'unitType', label: 'Módulo', labelKey: 'f_tipo_modulo', options: ['inicial', 'extension'], labels: MODULO_LABELS,
          labelKeys: { inicial: 'v_mod_inicial', extension: 'v_mod_adicional' } }
      ],
      defaultConfig: function () { return { alto: '200', ancho: '150', fondo: '60', niveles: 3, tipo: 'metalica', unitType: 'inicial' }; },
      nivelesFor: function (cfg) { return ALTO_NIVELES[String(cfg.alto || 200)] || [3, 4, 5, 6]; },
      // Requiere una Inicial COMPATIBLE (mismo alto y fondo) para poder añadir una
      // Extensión — "sin inicial no se añade extensión nunca" (regla del usuario).
      // v1: se exige que exista AL MENOS UNA en la sala, no necesariamente contigua
      // (el ensamblaje ya omite el bastidor izquierdo asumiendo que se acopla a una).
      // Contrato de CADENA (lo lee el motor; ver space-configurator.js). Declara qué
      // módulo lleva sus dos bastidores, cuál comparte el del vecino, y qué campos de
      // la config definen el BASTIDOR COMPARTIDO — que es lo que decide si dos módulos
      // pueden encadenar. El ancho NO entra: dos módulos de anchura distinta comparten
      // el mismo bastidor sin problema (cada uno trae sus largueros y su balda), y de
      // hecho el auto-relleno óptimo mezcla anchuras a propósito.
      chain: { full: 'inicial', shared: 'extension', matchKeys: ['alto', 'fondo'], sideGapMM: 0 },
      canUseUnitType: function (unitType, cfg, allItems) {
        return chainCompatible(this, unitType, cfg, allItems);
      },
      footprintMM: function (cfg) {
        var w = WIDTHS_MM[cfg.ancho] || 1500;
        if ((cfg.unitType || 'inicial') === 'extension') w -= MC_BASTIDOR_WIDTH_MM;
        return { width: w, depth: DEPTHS_MM[cfg.fondo] || 600, height: FRAME_HEIGHTS_MM[String(cfg.alto)] || 2000 };
      },
      kitUrl: function (urls) { return urls && urls.kit; },
      assemble: function (kitIndex, cfg, THREE) {
        var alto = String(cfg.alto), ancho = String(cfg.ancho), fondo = String(cfg.fondo), niv = cfg.niveles;
        var isExtension = (cfg.unitType || 'inicial') === 'extension';
        var zOff = MC_Z_OFFSETS[fondo] || 0.988;
        var group = new THREE.Group();

        function addPiece(name, yOverride) {
          var clone = cloneKitPiece(kitIndex, name, THREE);
          if (!clone) return null;
          clone.position.set(clone.position.x - MC_KIT_LEFT_X, (yOverride !== undefined) ? yOverride : clone.position.y, clone.position.z + zOff);
          clone.traverse(function (c) { if (c.isMesh) { c.castShadow = false; c.receiveShadow = false; } });
          group.add(clone);
          return clone;
        }

        // Inicial: lleva AMBOS bastidores. Extensión: omite el izquierdo (lo
        // comparte con el módulo Inicial al que se acopla) — huella más estrecha,
        // reflejada en footprintMM.
        if (!isExtension) addPiece(leftBastidorName(alto, fondo));
        addPiece('Bastidor' + alto + '.' + ancho + 'x' + fondo);

        var frontL = 'Larguero' + ancho + '.1';
        var backL = 'Larguero' + ancho + '.' + ancho + 'x' + fondo;
        var baldaN = 'Balda' + ancho + 'x' + fondo;
        for (var i = 0; i < niv; i++) {
          var yL = levelY(i, niv, alto);
          addPiece(frontL, yL);
          addPiece(backL, yL);
          addPiece(baldaN, yL + MC_BALDA_OFFSET_Y);
        }

        var refNames = MC_REFUERZOS[fondo + 'x' + ancho] || [];
        refNames.forEach(function (rn) { addPiece(rn); });

        applyColorToGroup(group, cfg);
        return group;
      },
      buildHandle: function (cfg) {
        var dims = cfg.alto + 'x' + cfg.ancho + 'x' + cfg.fondo + 'cm-' + cfg.niveles + '-niveles';
        var tipo = cfg.tipo || 'metalica';
        var adicional = (cfg.unitType === 'extension');
        if (tipo === 'aglomerado') {
          return (adicional
            ? 'modulo-adicional-para-estanteria-de-carga-pesada-con-aglomerado-de-18-mm-'
            : 'estanteria-para-picking-de-carga-pesada-con-aglomerado-de-18-mm-') + dims;
        }
        return (adicional
          ? 'modulo-adicional-para-estanterias-de-carga-pesada-con-baldas-metalicas-'
          : 'estanteria-metalica-de-carga-pesada-con-estantes-metalicos-') + dims;
      },
      deriveSku: function (cfg) {
        var niv = parseInt(cfg.niveles, 10); if (!niv) return null;
        var board = (cfg.tipo === 'aglomerado');
        var adic = (cfg.unitType === 'extension');
        var code = board ? (adic ? 2 : 1) : (adic ? 4 : 3);
        return 'DLO' + code + '-' + niv + '/' + p3(cfg.alto) + p3(cfg.ancho) + p3(cfg.fondo) + 'JG';
      },
      searchQueries: function (cfg) {
        var dims = cfg.alto + 'x' + cfg.ancho + 'x' + cfg.fondo;
        var niv = parseInt(cfg.niveles, 10);
        return [dims + 'cm ' + niv, dims + ' ' + niv, dims + 'cm', dims];
      },
      summary: function (cfg, tr) {
        return tr_(tr, 'gama_mediacarga_label', 'Media Carga (DLO)') + ' ' +
          fmtDim(WIDTHS_MM[cfg.ancho]) + '×' + fmtDim(DEPTHS_MM[cfg.fondo]) + '×' + cfg.alto + 'cm · ' +
          cfg.niveles + ' ' + tr_(tr, 'niveles_word', 'niveles') + ' · ' +
          tr_(tr, TIPO_KEYS[cfg.tipo], TIPO_LABELS[cfg.tipo] || cfg.tipo) + ' · ' +
          (cfg.unitType === 'extension' ? tr_(tr, 'v_mod_adicional', 'Extensión') : tr_(tr, 'v_mod_inicial', 'Inicial'));
      }
    };
  })();

  // ════════════════════════════════════════════════════════════════════════
  // PALLET RACKING (DPL) — kit de piezas nombradas, ensamblaje analítico.
  // Lógica portada de shopify/assets/configurator-pallet.js (producción, 616 productos
  // validados). Comparte con Media Carga el modo 'kit', pero se aparta de las otras cinco
  // gamas en tres cosas de fondo:
  //
  //   1. La capacidad se mide en PALÉS, no en kg por balda: 500|1000 kg POR PALÉ y
  //      (niveles + 1) × palés_por_hueco palés. El "+1" no es un error: el SUELO del hueco
  //      también es plano de almacenaje. ⚠️ NO se usa el metacampo
  //      `capacidad_de_carga_por_balda_kg`: solo está en 64 de los 616 productos y es
  //      incoherente (dice 3000 igual para ancho 185 que para 270). La ficha de producto
  //      también anuncia "3000 kg por balda" — no es fiable.
  //
  //   2. El reparto de niveles se MIDIÓ de los renders del catálogo, no se dedujo (ver
  //      levelY): espaciado uniforme entre largueros + un REMANENTE por encima del último.
  //
  //   3. Aquí NO existe el último recurso `viaHreflang()` de las otras cinco gamas, y no es
  //      un olvido: el mercado ES está en DRAFT, el primario es DE+AT, no se publica
  //      hreflang="es" y el handle español da 404 (medido). El respaldo real de esta gama
  //      son las searchQueries.
  //
  // DECISIÓN DE DISEÑO DEL PLANIFICADOR: el configurador de producto tiene un eje `modulos`
  // (packs de 1 a 4). Aquí cada ítem colocado es UN módulo (pack '01') y los packs se
  // consiguen ENCADENANDO ítems Inicial + Adicional, exactamente como ya hace Media Carga:
  // así el empaquetado, el arrastre y las cotas funcionan igual para todas las gamas. Por
  // eso no hay campo `modulos` — ni campo `fondo`, que es 110 cm en toda la gama.
  // ════════════════════════════════════════════════════════════════════════
  (function () {
    // RECORTE ESTRUCTURAL altura↔niveles, verificado contra los 616 productos reales. No es
    // una limitación de catálogo: es que a esas alturas solo existen esos niveles. Es
    // determinista → nivelesFor() lo resuelve sin gastar ni una petición.
    var ALTURA_NIVELES = { '300': [2], '350': [2, 3], '400': [2, 3], '500': [3], '600': [3, 4] };
    var PALES_POR_HUECO = { '185': 2, '270': 3 };   // por ancho de módulo (verificado)
    var CAP_MAX = 1000;                             // kg por palé, máximo de la gama
    var MODULOS_POR_ITEM = 1;                       // ver "DECISIÓN DE DISEÑO" arriba
    var FONDO_FIJO = '110';                         // toda la gama; no es selector

    // Cotas NOMINALES (las que ve el cliente): el 185/270 comercial es la LUZ LIBRE entre
    // puntales, no el paso entre ejes — solo se usan para el texto del resumen.
    var NOMINAL_WIDTHS_MM = { '185': 1850, '270': 2700 };
    var NOMINAL_DEPTH_MM = 1100;

    // ⚠️ Huecos REALES del catálogo en la familia de 500 kg (24 de los 256 combos de pack=1,
    // medidos sobre los 616 productos): carga 500 + alto 400 + 2 niveles (los dos anchos) y
    // carga 500 + alto 600 + 3 niveles + ancho 185. NO se codifican en nivelesFor() a
    // propósito: ese recorte es estructural (altura↔niveles) y estos son huecos de surtido de
    // UN eje distinto. En esas combinaciones el precio simplemente no resuelve.

    // ─── Constantes del kit (MEDIDAS sobre pallet-racking.glb, en metros) ──────────
    // El kit va horneado: cada nodo tiene scale 1, rotación identidad y su base en Y=0, así
    // que no hay transformaciones que compensar (a diferencia de BULLRACK).
    // Paso entre EJES de bastidor = luz libre (1,849 / 2,701) + ancho de perfil (0,080).
    var PAL_PITCH_X = { '185': 1.92936, '270': 2.78133 };
    var PAL_KIT_LEFT_X = 0;                 // el kit ya trae el eje del puntal en X=0
    var PAL_BASTIDOR_WIDTH = 0.11898;       // ancho en X del bastidor (lo marca la placa base)
    var PAL_BASTIDOR_WIDTH_MM = PAL_BASTIDOR_WIDTH * 1000;
    // Altura real de los bastidores en el GLB (medida, no nominal)
    var PAL_FRAME_HEIGHTS_M = { '300': 3.00498, '350': 3.50128, '400': 3.9997, '500': 4.99857, '600': 5.99841 };
    // ⚠️ Fondo del BBOX real del bastidor: Z de -1.21207 a -0.00130 = 1.21078 m → 1211 mm.
    // El 1.1014 que aparece documentado es la LUZ ENTRE PUNTALES (y 110 cm es el nominal
    // comercial), NO el bbox. Aquí manda el bbox: el motor usa footprint.depth para
    // colisiones y empaquetado, así que tiene que ser la huella física real.
    var PAL_DEPTH_MM = 1211;
    // ⚠️ Cara superior del PERFIL del larguero sobre la cota del nivel. NO es el alto de su
    // bbox (0,23493): ese lo marca el CONECTOR de los extremos, que sobresale 47,96 mm por
    // arriba. La malla se coloca en esta cota y queda ENRASADA con el canto.
    var LARGUERO_TOP = 0.18697;
    var PASO_RANURA = 0.050;                // rejilla de ranurado del puntal

    // ─── Colores reales del producto (la gama es BITONO) ──────────────────────────
    // El GLB llega SIN color y SIN NINGUNA TEXTURA (verificado: 0 imágenes / 0 texturas, 4
    // materiales planos), así que basta escribir color/roughness/metalness: aquí no hay mapas
    // que multipliquen el color programático ni `depthWrite:false` que vuelva transparente una
    // pieza — ese es el gotcha de BULLRACK, no de esta gama.
    var PAL_COLORS = {
      galva:     0xC9CDD2,  // galvanizado — bastidores y malla
      naranja:   0xE25303,  // NARANJA RAL 2004 — largueros
      protector: 0xF3C300   // AMARILLO RAL 1023 — protectores de puntal
    };
    var METAL_PROPS     = { roughness: 0.65, metalness: 0.18 };
    var RAL_PROPS       = { roughness: 0.60, metalness: 0.10 };  // pintura en polvo
    var PROTECTOR_PROPS = { roughness: 0.65, metalness: 0.05 };

    var MALLA_LABELS = { no: 'Sin balda', si: 'Malla galvanizada' };
    var SINO_LABELS = { no: 'No', si: 'Sí' };
    var CARGA_LABELS = { '500': '500 kg/palé', '1000': '1000 kg/palé' };
    var MODULO_LABELS = { inicial: 'Inicial (bastidores propios)', adicional: 'Adicional (comparte bastidor)' };

    function p2(n) { n = String(parseInt(n, 10) || 0); return n.length >= 2 ? n : ('00' + n).slice(-2); }
    // Los <select> del motor devuelven SIEMPRE cadenas ('si'/'no'), así que los dos ejes
    // booleanos de esta gama viven en la config como TEXTO. Se acepta también el booleano
    // porque el configurador de producto los guarda así y una config puede llegar de ahí
    // (permalink compartido, código): normalizar en un solo sitio evita que 'no' —que en JS
    // es truthy— acabe montando la malla.
    function on(v) { return v === true || v === 'si' || v === 'sí' || v === 'true' || v === 1 || v === '1'; }
    function esAdicional(cfg) { return (cfg.unitType || 'inicial') === 'adicional'; }
    // Infijo de ACABADO del SKU: '' liso · M malla · P protector · MP ambos
    function palAcabado(cfg) { return (on(cfg.malla) ? 'M' : '') + (on(cfg.protector) ? 'P' : ''); }
    function cargaDe(cfg) { return parseInt(cfg.carga, 10) === 500 ? 500 : 1000; }

    // Capacidad en palés: (niveles + 1) × palés por hueco × módulos del ítem (=1).
    function palesTotales(cfg) {
      var niv = parseInt(cfg.niveles, 10) || 0;
      var porHueco = PALES_POR_HUECO[String(cfg.ancho)] || 2;
      return (niv + 1) * porHueco * MODULOS_POR_ITEM;
    }

    // ─── Cota de cada larguero: MEDIDA de los renders del catálogo ────────────────
    // Dos repartos "razonables" salieron mal antes de dar con este, y los dos parecían
    // correctos sobre el papel:
    //   1. "N largueros con el primero a cota fija" → dejaba el hueco del suelo en 0,5 m,
    //      donde NO cabe un palé, contradiciendo la capacidad que anuncia la propia ficha.
    //   2. "N+1 huecos iguales" (razonando que el suelo también almacena) → correcto para la
    //      CAPACIDAD, pero no es donde van los largueros: deja el superior a media altura
    //      (60 % con 2 niveles) con un tercio del bastidor vacío por encima.
    // Lo bueno se MIDIÓ por análisis de píxel sobre los renders (filas naranjas = largueros),
    // con dos renders independientes por configuración coincidiendo dentro del 0,4 %:
    //   2 niveles (300 cm) → 40,3 · 79,2 % de la altura del bastidor
    //   4 niveles (600 cm) → 18,2 · 40,7 · 63,0 · 85,1 %
    // El patrón es ESPACIADO UNIFORME entre largueros + un REMANENTE por encima del último
    // (lo que deja el de arriba cerca de la cabeza del puntal), y la cota se redondea a la
    // rejilla del ranurado porque el larguero solo puede anclarse ahí.
    // ⚠️ Al verificar "separación uniforme" hay que tolerar UN paso de ranura (50 mm): el
    // redondeo puede dar 1,30 / 1,25 / 1,30 y con 2 mm de tolerancia la barrida falla en falso.
    var PAL_REMANENTE = { 2: 0.21, 3: 0.18, 4: 0.15 };   // fracción de la altura, por nº de niveles
    function levelY(idx, total, altoKey) {
      var n = Math.max(1, parseInt(total, 10) || 1);
      var frameH = PAL_FRAME_HEIGHTS_M[String(altoKey)] || PAL_FRAME_HEIGHTS_M['300'];
      var rem = PAL_REMANENTE[n] !== undefined ? PAL_REMANENTE[n] : 0.18;
      var paso = (frameH * (1 - rem)) / n;
      var pasos = Math.max(1, Math.round((paso * (idx + 1)) / PASO_RANURA));
      return pasos * PASO_RANURA;
    }

    // Mismo helper que Media Carga: three.js 0.134 SANEA los nombres al importar y elimina
    // los puntos, así que el índice del kit se consulta con el nombre sin puntos. Los nodos de
    // este kit no llevan puntos (Bastidor300, Larguero185F/T, Malla185, Protector), pero se
    // sanea igual para que un futuro 'Bastidor300.1' siga resolviendo.
    function cloneKitPiece(kitIndex, name) {
      var key = name.replace(/\./g, '');
      var src = kitIndex[key];
      if (!src) { console.warn('[SpaceCatalog/pallet] Pieza no encontrada en kit:', name); return null; }
      var clone = src.clone(true);
      clone.traverse(function (child) {
        if (child.isMesh && child.material) {
          child.material = Array.isArray(child.material)
            ? child.material.map(function (m) { return m.clone(); })
            : child.material.clone();
        }
      });
      return clone;
    }

    // ⚠️ La rama del PROTECTOR va ANTES del fallback. Si cae al `else` sale plateado y el
    // amarillo de seguridad desaparece sin que nada lo delate.
    // Se decide por NOMBRE DE NODO (no de material): el contrato de nombres del kit lo
    // controlamos nosotros. El nombre útil puede estar en la malla o en su nodo padre según
    // cómo clone three.js, así que se comprueban los dos.
    function applyColorToGroup(group) {
      group.traverse(function (child) {
        if (!child.isMesh || !child.material) return;
        var nombre = child.name || (child.parent && child.parent.name) || '';
        var hex, props;
        if (/^Protector/i.test(nombre)) { hex = PAL_COLORS.protector; props = PROTECTOR_PROPS; }
        else if (/^Larguero/i.test(nombre)) { hex = PAL_COLORS.naranja; props = RAL_PROPS; }
        else { hex = PAL_COLORS.galva; props = METAL_PROPS; }  // bastidores, malla y futuras piezas
        var mats = Array.isArray(child.material) ? child.material : [child.material];
        mats.forEach(function (m) {
          if (!m || !m.color) return;
          m.color.setHex(hex);
          if (props.roughness !== undefined && m.roughness !== undefined) m.roughness = props.roughness;
          if (props.metalness !== undefined && m.metalness !== undefined) m.metalness = props.metalness;
          m.needsUpdate = true;
        });
      });
    }

    // Handle español determinista. Verificado 616/616 contra los handles reales de
    // shopify/scripts/output/pallet-all-raw.json (incluidos los packs de 2-4 módulos, que el
    // planificador no usa). ⚠️ En producción este handle da 404 para TODO visitante: el
    // mercado ES está en DRAFT y la raíz redirige a /de-de/ — el motor lo intenta primero y
    // cae a searchQueries, que es el camino real de esta gama.
    function palHandle(cfg) {
      var mods = MODULOS_POR_ITEM;
      var h = 'estanteria-para-palets-modulo-' + (esAdicional(cfg) ? 'adicional' : 'inicial') +
        '-' + cfg.alto + 'x' + cfg.ancho + 'x' + FONDO_FIJO + 'cm-' + cfg.niveles + '-niveles-' +
        cargaDe(cfg) + 'kg-por-paleta-' + mods + '-' + (mods === 1 ? 'modulo' : 'modulos');
      if (on(cfg.malla)) h += '-malla-metalica';
      if (on(cfg.protector)) h += '-protector-puntal';
      return h;
    }

    GAMAS.pallet = {
      id: 'pallet', label: 'Estantería para palés', loadMode: 'kit',
      labelKey: 'gama_pallet_label', useCaseKey: 'gama_pallet_use',
      description: 'Palés con carretilla elevadora: almacenes logísticos y almacenamiento a granel.',
      // Pasillo mínimo operativo: esta gama se maneja con carretilla elevadora, que
      // necesita ~2,8 m para girar y depositar el palé. Con los 90 cm por defecto
      // (que valen para picking a mano) la distribución sale geométricamente válida
      // pero inservible en la práctica. El motor sube el pasillo del panel al elegir
      // la gama y avisa de que lo ha hecho.
      minAisleMM: 2800,
      // Capacidad POR PALÉ + cuántos palés entran en este módulo (formato con count/countUnitKey).
      capacity: function (cfg) {
        return { value: cargaDe(cfg), unitKey: 'cap_unit_pallet', count: palesTotales(cfg), countUnitKey: 'pales_word' };
      },
      capacityMax: function () { return { value: CAP_MAX, unitKey: 'cap_unit_pallet' }; },
      fields: [
        { key: 'ancho', label: 'Anchura de módulo', unit: 'cm', options: ['185', '270'] },
        { key: 'alto', label: 'Altura', unit: 'cm', options: ['300', '350', '400', '500', '600'] },
        { key: 'niveles', label: 'Niveles', dynamic: true },
        /* Los 4 ejes propios de esta gama REUTILIZAN las claves del configurador de
           producto de palés (`configurator.options.*` / `values.*`), ya traducidas en los
           10 locales — cero traducciones nuevas. La ficha etiqueta el eje de la malla
           como "Tipo de balda" (f_tipo_balda), y la carga la compone con el número + la
           unidad ya traducida (`cap_unit_pallet` = kg/palé) para no tener una clave por
           cifra; por eso este campo YA NO declara `unit: 'kg'` (era redundante). */
        { key: 'malla', label: 'Balda de malla', labelKey: 'f_tipo_balda', options: ['no', 'si'], labels: MALLA_LABELS,
          labelKeys: { no: 'v_malla_no', si: 'v_malla_si' } },
        { key: 'protector', label: 'Protector de puntal', labelKey: 'f_protector', options: ['no', 'si'], labels: SINO_LABELS,
          labelKeys: { no: 'v_no', si: 'v_si' } },
        { key: 'carga', label: 'Carga por palé', labelKey: 'f_carga_pale', options: ['500', '1000'], labels: CARGA_LABELS,
          valueSuffixKey: 'cap_unit_pallet' },
        { key: 'unitType', label: 'Módulo', labelKey: 'f_tipo_modulo', options: ['inicial', 'adicional'], labels: MODULO_LABELS,
          labelKeys: { inicial: 'v_mod_inicial', adicional: 'v_mod_adicional' } }
      ],
      defaultConfig: function () {
        return { alto: '300', ancho: '185', fondo: FONDO_FIJO, niveles: 2,
                 malla: 'no', protector: 'no', carga: '1000', unitType: 'inicial' };
      },
      // Al recorte estructural altura↔niveles se le suma un HUECO DE SURTIDO real de la
      // familia de 500 kg: esas tres combinaciones existen con 1000 kg pero NO con 500,
      // así que se retiran de las opciones en vez de dejar que el cliente las elija y
      // se quede sin precio. Verificado contra los 616 SKU reales de la gama
      // (shopify/scripts/output/pallet-all-raw.json): son 24 combinaciones = 3 grupos ×
      // 4 acabados × 2 tipos de módulo. Si algún día se dan de alta esos productos,
      // basta con borrar HUECOS_500 y su filtro.
      //   500 kg + alto 400 + 2 niveles  (anchos 185 y 270)
      //   500 kg + alto 600 + 3 niveles  (solo ancho 185; con 270 sí existe)
      nivelesFor: function (cfg) {
        var base = ALTURA_NIVELES[String(cfg.alto || 300)] || [2];
        if (String(cfg.carga) !== '500') return base;
        var alto = String(cfg.alto || 300), ancho = String(cfg.ancho || 185);
        return base.filter(function (n) {
          if (alto === '400' && n === 2) return false;
          if (alto === '600' && n === 3 && ancho === '185') return false;
          return true;
        });
      },
      // Un Adicional comparte el bastidor izquierdo con el módulo de su izquierda, así que
      // exige que exista al menos un Inicial COMPATIBLE (mismo alto y mismo ancho de módulo)
      // en la sala — misma regla que Media Carga ("sin inicial no se añade adicional nunca").
      // Misma cadena que Media Carga (ver su comentario): un Adicional comparte el
      // bastidor izquierdo con el módulo de su izquierda. El bastidor lo definen alto y
      // fondo — y aquí el fondo es SIEMPRE 110, así que en la práctica manda el alto.
      // ⚠️ Antes esta regla exigía además mismo ANCHO, que es más estricto que el
      // producto real y además rompía las filas de anchura mezclada del auto-relleno
      // óptimo: cada cambio de anchura dejaba el Adicional sin Inicial y el motor lo
      // degradaba a Inicial (cada módulo con sus dos bastidores = ~25% más caro).
      chain: { full: 'inicial', shared: 'adicional', matchKeys: ['alto', 'fondo'], sideGapMM: 0 },
      canUseUnitType: function (unitType, cfg, allItems) {
        return chainCompatible(this, unitType, cfg, allItems);
      },
      // Ancho = los huecos del ítem (paso entre ejes de puntal × módulos), y un Adicional
      // descuenta el bastidor que comparte — mismo criterio que Media Carga, que es lo que
      // hace que un run Inicial+Adicional+… encaje sin huecos ni solapes. El bbox real de un
      // Inicial es un bastidor MÁS ancho que esto (el de su extremo derecho, que cede al
      // vecino); usar el paso es justo lo que evita contar ese bastidor dos veces.
      footprintMM: function (cfg) {
        var pitch = PAL_PITCH_X[String(cfg.ancho)] || PAL_PITCH_X['185'];
        var w = pitch * 1000 * MODULOS_POR_ITEM;
        if (esAdicional(cfg)) w -= PAL_BASTIDOR_WIDTH_MM;
        return {
          width: w,
          depth: PAL_DEPTH_MM,
          height: (PAL_FRAME_HEIGHTS_M[String(cfg.alto)] || PAL_FRAME_HEIGHTS_M['300']) * 1000
        };
      },
      kitUrl: function (urls) { return urls && urls.kit; },
      assemble: function (kitIndex, cfg, THREE) {
        var alto = String(cfg.alto), ancho = String(cfg.ancho);
        var niv = parseInt(cfg.niveles, 10) || 2;
        var mods = MODULOS_POR_ITEM;
        var paso = PAL_PITCH_X[ancho] || PAL_PITCH_X['185'];
        var group = new THREE.Group();

        // Búsqueda por IGUALDAD EXACTA del nombre, nunca por substring: así no hay forma de
        // que un futuro 'Larguero1850' se cuele donde toca 'Larguero185'.
        function addPiece(name, x, yOverride) {
          var clone = cloneKitPiece(kitIndex, name);
          if (!clone) return null;
          clone.position.set(
            (x || 0) - PAL_KIT_LEFT_X,
            (yOverride !== undefined) ? yOverride : clone.position.y,
            clone.position.z
          );
          clone.traverse(function (c) { if (c.isMesh) { c.castShadow = false; c.receiveShadow = false; } });
          group.add(clone);
          return clone;
        }

        // ── Bastidores: N huecos → N+1 bastidores. Un Adicional omite el IZQUIERDO porque lo
        // comparte con el módulo previo (es la semántica de las familias 2 y 4 del SKU).
        var desdeHueco = esAdicional(cfg) ? 1 : 0;
        for (var b = desdeHueco; b <= mods; b++) addPiece('Bastidor' + alto, b * paso);

        // ── Niveles: larguero delantero + trasero por hueco, y malla si el acabado la lleva.
        // Los largueros F y T ya traen su profundidad HORNEADA → no se mueven en Z.
        for (var h = 0; h < mods; h++) {
          var xh = h * paso;
          for (var i = 0; i < niv; i++) {
            var yL = levelY(i, niv, alto);
            addPiece('Larguero' + ancho + 'F', xh, yL);
            addPiece('Larguero' + ancho + 'T', xh, yL);
            if (on(cfg.malla)) addPiece('Malla' + ancho, xh, yL + LARGUERO_TOP);
          }
        }

        // ── Protectores de puntal: uno por bastidor DIBUJADO, a nivel de suelo ──
        if (on(cfg.protector)) {
          for (var p = desdeHueco; p <= mods; p++) addPiece('Protector', p * paso, 0);
        }

        applyColorToGroup(group);
        return group;
      },
      buildHandle: palHandle,
      // DPL{M|P|MP}?{familia}{pack2}-{niveles}/{alto}{ancho}{fondo}ZG
      //   familia: 1 inicial 1000kg · 2 adicional 1000kg · 3 inicial 500kg · 4 adicional 500kg
      //   pack: nº de módulos a 2 cifras — aquí siempre '01' (un módulo por ítem)
      // El SKU no se traduce → identifica el producto equivalente en cualquier mercado, y es
      // el único filtro que de verdad decide cuál de los candidatos de búsqueda es el bueno.
      // Verificado 616/616 contra los SKU reales del catálogo.
      deriveSku: function (cfg) {
        var niv = parseInt(cfg.niveles, 10); if (!niv) return null;
        var adic = esAdicional(cfg) ? 1 : 0;
        var familia = (cargaDe(cfg) === 500 ? 3 : 1) + adic;
        return 'DPL' + palAcabado(cfg) + familia + p2(MODULOS_POR_ITEM) + '-' + niv + '/' +
          p3(cfg.alto) + p3(cfg.ancho) + p3(FONDO_FIJO) + 'ZG';
      },
      // Sin viaHreflang() (ver cabecera), estas queries SON el respaldo de la gama, así que
      // van de más selectiva a más laxa y cada candidato se valida por SKU exacto.
      //   · El token de dimensiones viaja igual en todos los mercados: los handles/títulos
      //     traducidos lo llevan como '300x185x110' (de: palettenregal-grundregal-300x185x110-…,
      //     pl: regaly-paletowe-300x185x110-…) — el sufijo 'cm' solo aparece en castellano.
      //   · La carga (500/1000) también es lenguaje-neutro y está en el título alemán, así que
      //     la primera query recorta el ruido de los 64 productos que comparten dimensiones y
      //     niveles (el tope DURO de 10 resultados de suggest.json es lo que deja fuera al
      //     bueno cuando la query es poco selectiva).
      //   · El handle en castellano va ÚLTIMO, no primero como en el configurador de producto:
      //     allí se usaba el handle YA TRADUCIDO del metacampo `option_groups_data`, que aquí
      //     no tenemos (el planificador no aterriza en ninguna ficha).
      searchQueries: function (cfg) {
        var dims = cfg.alto + 'x' + cfg.ancho + 'x' + FONDO_FIJO;
        var niv = parseInt(cfg.niveles, 10);
        return [
          dims + ' ' + niv + ' ' + cargaDe(cfg),
          dims + ' ' + niv,
          dims + 'cm ' + niv,
          dims,
          palHandle(cfg).replace(/-/g, ' ')
        ];
      },
      summary: function (cfg, tr) {
        return tr_(tr, 'gama_pallet_label', 'Estantería para palés') + ' ' +
          fmtDim(NOMINAL_WIDTHS_MM[cfg.ancho] || 1850) + '×' +
          fmtDim(NOMINAL_DEPTH_MM) + '×' + cfg.alto + 'cm · ' +
          cfg.niveles + ' ' + tr_(tr, 'niveles_word', 'niveles') + ' · ' +
          cargaDe(cfg) + ' ' + tr_(tr, 'cap_unit_pallet', 'kg/palé') + ' · ' +
          palesTotales(cfg) + ' ' + tr_(tr, 'pales_word', 'palés') +
          (on(cfg.malla) ? ' · ' + tr_(tr, 'v_malla_si', MALLA_LABELS.si) : '') +
          (on(cfg.protector) ? ' · ' + tr_(tr, 'f_protector', 'Protector de puntal') : '') +
          (esAdicional(cfg) ? ' · ' + tr_(tr, 'v_mod_adicional', 'Adicional') : ' · ' + tr_(tr, 'v_mod_inicial', 'Inicial'));
      }
    };
  })();

  // BULLRACK va detrás de RIVET: es su hermana de carga pesada (mismo montaje sin
  // tornillos, más carga por balda y anchos de hasta 2 m). SINTORNILLOS va detrás de
  // VERSATILE: son las dos gamas de estantería metálica ligera de las mismas medidas, una
  // atornillada (DMO1) y otra a presión (DVL1). PALLET cierra la lista: es la gama más
  // grande (bastidores de hasta 6 m) y la única que se mide en palés.
  // ⚠️ El `id` 'sinTornillos' (camelCase, como 'mediaCarga') tiene que ser IDÉNTICO aquí, en
  // GAMAS, en `glbUrls` y en `gamaImages` del Liquid. Sus claves i18n van en minúscula
  // ('gama_sintornillos_label'), igual que mediaCarga → 'gama_mediacarga_label'.
  window.SpaceCatalog = { GAMAS: GAMAS, order: ['rivet', 'bullrack', 'versatile', 'sinTornillos', 'mediaCarga', 'pallet'] };

})();
